import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, X, Send, Camera, User, Users, MessageSquare, Image, Calendar, LogOut, ChevronRight, ChevronDown, Check, Clock, CheckCircle, BarChart3, Bell, Plus, Wallet, Images, ArrowUp, ArrowRight, Edit, Settings, HelpCircle, ExternalLink, Upload, MapPin, DollarSign, Star, Sparkles, Zap, Crown, CalendarPlus, FileText, CreditCard, ChevronLeft, Heart, Download, Lock, CloudDownload, Copy, Menu, Eye, ImagePlus } from "lucide-react";
import ProRedesignSections from "@/components/ProRedesignSections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const resolveImageUrl = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${img}`;
  }
  return img;
};

const CLOUDINARY_IMAGE_PRESETS = {
  avatar: 'c_fill,g_auto,w_520,h_520,dpr_auto,f_auto,q_auto',
  card: 'c_fill,g_auto,w_720,h_960,dpr_auto,f_auto,q_auto',
  thumb: 'c_fill,g_auto,w_640,h_640,dpr_auto,f_auto,q_auto',
  preview: 'c_fill,g_auto,w_1100,h_720,dpr_auto,f_auto,q_auto',
  hero: 'c_fill,g_auto,w_2200,h_1300,dpr_auto,f_auto,q_auto',
  gallery: 'c_fill,g_auto,w_1200,h_1500,dpr_auto,f_auto,q_auto',
  full: 'c_limit,w_2400,dpr_auto,f_auto,q_auto'
};

const getOptimizedImageUrl = (img, preset = 'thumb') => {
  const url = resolveImageUrl(img);
  const transform = CLOUDINARY_IMAGE_PRESETS[preset] || preset;
  if (!url || !transform) return url;

  try {
    const parsed = new URL(url);
    if (!parsed.hostname.endsWith('cloudinary.com') || !parsed.pathname.includes('/image/upload/')) {
      return url;
    }

    const marker = '/image/upload/';
    const [prefix, suffix] = url.split(marker);
    if (!prefix || !suffix) return url;

    const pathParts = suffix.split('/');
    const versionIndex = pathParts.findIndex((part) => /^v\d+$/.test(part));
    const assetPath = versionIndex >= 0 ? pathParts.slice(versionIndex).join('/') : suffix;

    return `${prefix}${marker}${transform}/${assetPath}`;
  } catch {
    return url;
  }
};

const getGalleryShareUrl = (galleryId) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/gallery/${galleryId}`;
};

const PUBLIC_API_TIMEOUT_MS = 5500;
const AUTH_API_TIMEOUT_MS = 10000;

const HOMEPAGE_FALLBACK_ARTISTS = [
  {
    id: 'fallback-aura',
    name: 'Aura Studio',
    category: 'Editorial',
    location: 'Paris',
    img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=1200',
    hourly_rate: 250
  },
  {
    id: 'fallback-julian',
    name: 'Julian Vane',
    category: 'Wedding',
    location: 'NYC',
    img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 300
  },
  {
    id: 'fallback-elena',
    name: 'Elena Rossi',
    category: 'Fashion',
    location: 'Milan',
    img: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 350
  },
  {
    id: 'fallback-marcus',
    name: 'Marcus Thorne',
    category: 'Portrait',
    location: 'London',
    img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 200
  },
  {
    id: 'fallback-sasha',
    name: 'Sasha V.',
    category: 'Editorial',
    location: 'Berlin',
    img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 275
  },
  {
    id: 'fallback-leo',
    name: 'Leo Castiglione',
    category: 'Fashion',
    location: 'Rome',
    img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 320
  },
  {
    id: 'fallback-maya',
    name: 'Maya Sun',
    category: 'Wedding',
    location: 'Bali',
    img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 280
  },
  {
    id: 'fallback-oliver',
    name: 'Oliver North',
    category: 'Portrait',
    location: 'Toronto',
    img: 'https://images.unsplash.com/photo-1552374196-c4e7ffc6e126?auto=format&fit=crop&q=80&w=800',
    hourly_rate: 180
  }
];

const PRO_SECTIONS = new Set([
  'dashboard',
  'galleries',
  'bookings',
  'messages',
  'portfolio',
  'availability',
  'profile',
  'earnings',
  'membership'
]);

const getProSectionFromRoute = (section) => {
  if (!section || section === 'dashboard') return 'dashboard';
  if (section === 'private-gallery') return 'galleries';
  return PRO_SECTIONS.has(section) ? section : 'dashboard';
};

const clearStoredAuth = () => {
  localStorage.removeItem('aura_user');
  localStorage.removeItem('aura_token');
  localStorage.removeItem('aura_user_type');
  localStorage.removeItem('session_token');
};

const getInitialAuthState = () => {
  const storedUser = localStorage.getItem('aura_user');
  const storedToken = localStorage.getItem('aura_token');
  const storedType = localStorage.getItem('aura_user_type');
  if (!storedUser || !storedToken || !['client', 'photographer'].includes(storedType)) {
    clearStoredAuth();
    return { token: null, user: null, userType: null };
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    if (!parsedUser || typeof parsedUser !== 'object') throw new Error('Invalid stored user');
    return { token: storedToken, user: parsedUser, userType: storedType };
  } catch {
    clearStoredAuth();
    return { token: null, user: null, userType: null };
  }
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const renderMessageContent = (content, linkClassName = '') => {
  const parts = String(content || '').split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName || 'underline underline-offset-2'}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [initialAuthState] = useState(getInitialAuthState);
  const [user, setUser] = useState(initialAuthState.user);
  const [userType, setUserType] = useState(initialAuthState.userType);
  const [loading, setLoading] = useState(false);
  const [authToken, setAuthToken] = useState(initialAuthState.token);

  const login = (authResponse) => {
    setAuthToken(authResponse.token);
    setUser(authResponse.user);
    setUserType(authResponse.user_type);
    localStorage.setItem('aura_token', authResponse.token);
    localStorage.setItem('aura_user', JSON.stringify(authResponse.user));
    localStorage.setItem('aura_user_type', authResponse.user_type);
  };

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setUserType(null);
    clearStoredAuth();
  };

  const getAuthHeader = () => {
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, userType, token: authToken, login, logout, getAuthHeader, loading, isAuthenticated: !!authToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== API FUNCTIONS ====================
const api = {
  getPhotographers: async (category, search) => {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (search) params.append('search', search);
    const response = await axios.get(`${API}/photographers?${params}`, {
      timeout: PUBLIC_API_TIMEOUT_MS
    });
    return response.data;
  },
  getPhotographer: async (id) => {
    const response = await axios.get(`${API}/photographers/${id}`);
    return response.data;
  },
  // Client Auth
  registerClient: async (data) => {
    const response = await axios.post(`${API}/clients/register`, data, {
      timeout: AUTH_API_TIMEOUT_MS
    });
    return response.data;
  },
  loginClient: async (data) => {
    const response = await axios.post(`${API}/clients/login`, data, {
      timeout: AUTH_API_TIMEOUT_MS
    });
    return response.data;
  },
  // Photographer Auth
  registerPhotographer: async (data) => {
    const response = await axios.post(`${API}/photographers/register`, data, {
      timeout: AUTH_API_TIMEOUT_MS
    });
    return response.data;
  },
  loginPhotographer: async (data) => {
    const response = await axios.post(`${API}/photographers/login`, data, {
      timeout: AUTH_API_TIMEOUT_MS
    });
    return response.data;
  },
  // Conversations
  getConversations: async (token) => {
    const response = await axios.get(`${API}/conversations`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  createConversation: async (photographerId, token) => {
    const response = await axios.post(`${API}/conversations?photographer_id=${photographerId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  // Messages
  getMessages: async (conversationId, token) => {
    const response = await axios.get(`${API}/messages/${conversationId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  sendMessage: async (data, token) => {
    const response = await axios.post(`${API}/messages`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  declineBookingRequest: async (bookingId, reason, token) => {
    const response = await axios.post(`${API}/requests/${bookingId}/decline`, { reason }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  acceptBookingRequest: async (bookingId, photographerAmount, token) => {
    const response = await axios.post(`${API}/requests/${bookingId}/accept`, { photographerAmount }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  createInvoice: async (bookingId, photographerAmount, token) => {
    const response = await axios.post(`${API}/invoices`, {
      requestId: bookingId,
      photographerAmount
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  signContract: async (contractId, signature, token) => {
    const response = await axios.post(`${API}/contracts/${contractId}/sign`, { signature }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  payInvoice: async (invoiceId, bookingId, token) => {
    const response = await axios.post(`${API}/payments/charge`, { invoiceId, booking_id: bookingId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  sendPreviewGallery: async (bookingId, data, token) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/preview-gallery`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  addGalleryComment: async (galleryId, imageId, data, token) => {
    const response = await axios.post(`${API}/galleries/${galleryId}/images/${imageId}/comments`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  finalSignoff: async (bookingId, token) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/final-signoff`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  sendFinalGallery: async (bookingId, data, token) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/final-gallery`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  shareGalleryLink: async (galleryId, data, token) => {
    const response = await axios.post(`${API}/galleries/${galleryId}/share`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  // Jobs
  getJobs: async (token) => {
    const response = await axios.get(`${API}/jobs`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  createJob: async (data, token) => {
    const response = await axios.post(`${API}/jobs`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updateJob: async (jobId, data, token) => {
    const response = await axios.put(`${API}/jobs/${jobId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  // Photographer Dashboard
  getPhotographerStats: async (token) => {
    const response = await axios.get(`${API}/photographers/stats/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updatePhotographerProfile: async (data, token) => {
    const response = await axios.put(`${API}/photographers/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  uploadProfilePhoto: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/photographers/upload-photo`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  uploadPortfolioImage: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/photographers/upload-portfolio`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  uploadGalleryImage: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/galleries/upload-image`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  // Subscription & Boosts
  getSubscription: async (token) => {
    const response = await axios.get(`${API}/subscription`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  changePlan: async (plan, token) => {
    const response = await axios.post(`${API}/subscription/change-plan?plan=${plan}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  activateBoost: async (token) => {
    const response = await axios.post(`${API}/subscription/activate-boost`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  purchaseBoosts: async (packageType, token) => {
    const response = await axios.post(`${API}/subscription/purchase-boosts?package=${packageType}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  // Booking & Availability APIs
  getPhotographerAvailability: async (photographerId, month, year) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    const response = await axios.get(`${API}/photographers/${photographerId}/availability?${params}`);
    return response.data;
  },
  getMyAvailability: async (token) => {
    const response = await axios.get(`${API}/photographers/me/availability`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updateMyAvailability: async (availableDates, token) => {
    const response = await axios.put(`${API}/photographers/me/availability`, 
      { available_dates: availableDates },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  createBooking: async (data, token) => {
    const response = await axios.post(`${API}/bookings`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getBookings: async (token) => {
    const response = await axios.get(`${API}/bookings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getBooking: async (bookingId, token) => {
    const response = await axios.get(`${API}/bookings/${bookingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  approveBooking: async (bookingId, token) => {
    const response = await axios.put(`${API}/bookings/${bookingId}/approve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  rejectBooking: async (bookingId, token) => {
    const response = await axios.put(`${API}/bookings/${bookingId}/reject`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  signBooking: async (bookingId, signature, token) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/sign`, 
      { signature },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  payBookingDeposit: async (bookingId, token) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/pay-deposit`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  // Google OAuth APIs
  processGoogleSession: async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/session?session_id=${sessionId}`);
    return response.data;
  },
  getAuthMe: async (token) => {
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  authLogout: async (token) => {
    const response = await axios.post(`${API}/auth/logout`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  // Admin APIs
  getSiteConfig: async () => {
    const response = await axios.get(`${API}/admin/site-config`);
    return response.data;
  },
  updateSiteConfig: async (data, token) => {
    const response = await axios.put(`${API}/admin/site-config`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAdminPhotographers: async (token, search, tier) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (tier) params.append('tier', tier);
    const response = await axios.get(`${API}/admin/photographers?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAdminPhotographer: async (photographerId, token) => {
    const response = await axios.get(`${API}/admin/photographers/${photographerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updateAdminPhotographer: async (photographerId, data, token) => {
    const response = await axios.put(`${API}/admin/photographers/${photographerId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAdminStats: async (token) => {
    const response = await axios.get(`${API}/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  makeAdmin: async (photographerId, token) => {
    const response = await axios.post(`${API}/admin/make-admin/${photographerId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  revokeAdmin: async (photographerId, token) => {
    const response = await axios.post(`${API}/admin/revoke-admin/${photographerId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// ==================== AUTH CALLBACK COMPONENT ====================
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Get session_id from URL hash
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in URL');
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // Exchange session_id for user data
        const data = await api.processGoogleSession(sessionId);
        
        // Login with the JWT token for compatibility
        login({
          token: data.jwt_token,
          user_type: data.user_type,
          user: data.user
        });

        // Store session token as well
        localStorage.setItem('session_token', data.session_token);

        // Clear the hash and redirect to dashboard
        window.history.replaceState(null, '', window.location.pathname);
        navigate(data.user_type === 'photographer' ? '/pro' : '/dashboard', { state: { user: data.user } });
      } catch (error) {
        console.error('Failed to process Google auth:', error);
        navigate('/login');
      }
    };

    processAuth();
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center aura-site">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900 mx-auto mb-4"></div>
        <p className="text-stone-500">Signing you in...</p>
      </div>
    </div>
  );
};

// ==================== NAVBAR COMPONENT ====================
const Navbar = ({ onOpenChat, searchValue, onSearchChange }) => {
  const { user, userType, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const dashboardPath = userType === 'photographer' ? '/pro' : '/dashboard';
  const displayName = isAuthenticated ? (user?.name || 'Aura Member') : 'Enter Aura';
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const profileImage = user?.avatar || user?.img;

  const handleAccountClick = () => {
    navigate(isAuthenticated ? dashboardPath : '/login');
  };

  const handleBookingClick = () => {
    navigate(isAuthenticated ? dashboardPath : '/register');
  };

  return (
    <nav className="fixed w-full z-50 glass aura-nav" data-testid="navbar">
      <div className="aura-nav-shell max-w-[1740px] mx-auto px-5 sm:px-8 flex items-center justify-between">
        <div className="aura-nav-left flex items-center">
          <Link to="/" className="aura-logo text-2xl md:text-4xl serif" data-testid="logo">
            AURA
          </Link>
          <div className="aura-nav-search hidden lg:flex relative transition-all duration-300">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-stone-500 w-5 h-5" />
            <input
              type="text"
              data-testid="search-input"
              value={searchValue || ''}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              placeholder="Search by name or style..."
              className="aura-search pl-16 pr-7 border rounded-full text-base w-[400px] focus:outline-none"
            />
          </div>
        </div>

        <div className="aura-nav-links hidden xl:flex items-center">
          <a href="#discover" className="aura-secondary-link">Discover</a>
          <a href="#discover" className="aura-secondary-link">Artists</a>
          <button onClick={() => navigate(isAuthenticated ? dashboardPath : '/register')} className="aura-secondary-link">
            Collections
          </button>
          <a href="#journal" className="aura-secondary-link">Journal</a>
          <button onClick={handleBookingClick} className="aura-secondary-link">
            Bookings
          </button>
          {isAuthenticated && (
            <button onClick={onOpenChat} className="aura-secondary-link relative" data-testid="messages-btn">
              Messages
            </button>
          )}
        </div>

        <div className="aura-nav-actions flex items-center">
          <button
            onClick={handleAccountClick}
            className="aura-account-chip"
            data-testid={isAuthenticated ? 'dashboard-link' : 'signin-link'}
          >
            <span className="aura-account-avatar">
              {profileImage ? (
                <img src={resolveImageUrl(profileImage)} alt={displayName} />
              ) : (
                initials
              )}
            </span>
            <span className="aura-account-name hidden sm:inline">{displayName}</span>
            <ChevronDown className="hidden sm:block w-4 h-4 text-stone-600" />
          </button>
          <div className="relative">
            <button
              onClick={() => setMenuOpen((current) => !current)}
              className="aura-menu-button"
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <Menu className="w-5 h-5" />
            </button>
            {menuOpen && (
              <div className="aura-menu-popover">
                <a href="#discover" onClick={() => setMenuOpen(false)}>Discover artists</a>
                <button onClick={() => { setMenuOpen(false); handleBookingClick(); }}>Bookings</button>
                {isAuthenticated && (
                  <button onClick={() => { setMenuOpen(false); onOpenChat && onOpenChat(); }}>Messages</button>
                )}
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                      navigate('/');
                    }}
                    data-testid="logout-btn"
                  >
                    Sign out
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/register');
                    }}
                    data-testid="register-btn"
                  >
                    Join Aura
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// ==================== CHAT DRAWER COMPONENT ====================
const ChatDrawer = ({ isOpen, onClose, photographer }) => {
  const { token, isAuthenticated, user, userType } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loadConversations = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const convs = await api.getConversations(token);
      setConversations(convs);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!token) return;
    try {
      const msgs = await api.getMessages(conversationId, token);
      setMessages(msgs);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }, [token]);

  const startConversationWithPhotographer = useCallback(async () => {
    if (!photographer || !token) return;
    try {
      const conv = await api.createConversation(photographer.id, token);
      setSelectedConversation(conv);
      loadMessages(conv.id);
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  }, [photographer, token, loadMessages]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadConversations();
    }
  }, [isOpen, isAuthenticated, loadConversations]);

  useEffect(() => {
    if (photographer && isAuthenticated && isOpen && userType === 'client') {
      startConversationWithPhotographer();
    }
  }, [photographer, isAuthenticated, isOpen, userType, startConversationWithPhotographer]);

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv.id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation || !token) return;
    const outgoingMessage = newMessage.trim();

    setIsSending(true);
    try {
      const message = await api.sendMessage({
        photographer_id: selectedConversation.photographer_id,
        conversation_id: selectedConversation.id,
        content: outgoingMessage
      }, token);
      setMessages((currentMessages) => [...currentMessages, message]);
      setNewMessage('');
      loadConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  if (!isAuthenticated) {
    return (
	    <div
	        data-testid="chat-drawer"
	        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[100] flex flex-col transition-transform duration-400 ${
	          isOpen ? 'translate-x-0 visible pointer-events-auto' : 'translate-x-full invisible pointer-events-none'
	        }`}
	      >
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="font-bold italic serif text-xl">Messages</h3>
          <button onClick={onClose} data-testid="close-chat-btn">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <MessageSquare className="w-16 h-16 text-stone-200 mb-4" />
          <h4 className="text-xl font-bold mb-2">Sign in to message</h4>
          <p className="text-stone-500 mb-6">Create an account to start chatting with photographers</p>
          <button
            onClick={() => { onClose(); navigate('/register'); }}
            className="bg-stone-900 text-white px-6 py-3 rounded-full font-medium"
            data-testid="chat-register-btn"
          >
            Create Account
          </button>
          <button
            onClick={() => { onClose(); navigate('/login'); }}
            className="mt-3 text-stone-600 font-medium"
          >
            Already have an account? Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
	    <div
	      data-testid="chat-drawer"
	      className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[100] flex flex-col transition-transform duration-400 ${
	        isOpen ? 'translate-x-0 visible pointer-events-auto' : 'translate-x-full invisible pointer-events-none'
	      }`}
	    >
      <div className="p-6 border-b flex justify-between items-center">
        <h3 className="font-bold italic serif text-xl">
          {selectedConversation 
            ? (userType === 'client' ? selectedConversation.photographer_name : selectedConversation.client_name)
            : 'Messages'
          }
        </h3>
        <div className="flex items-center space-x-2">
          {selectedConversation && (
            <button 
              onClick={() => setSelectedConversation(null)}
              className="text-stone-500 hover:text-stone-900"
            >
              Back
            </button>
          )}
          <button onClick={onClose} data-testid="close-chat-btn">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {!selectedConversation ? (
        // Conversation List
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-stone-500">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center">
              <MessageSquare className="w-12 h-12 text-stone-200 mx-auto mb-3" />
              <p className="text-stone-500">No conversations yet</p>
              <p className="text-sm text-stone-400 mt-1">Start chatting with a photographer!</p>
            </div>
          ) : (
            <div className="divide-y">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className="w-full p-4 flex items-center space-x-3 hover:bg-stone-50 text-left"
                  data-testid={`conversation-${conv.id}`}
                >
                  {conv.other_user_img ? (
                    <img src={conv.other_user_img} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center">
                      <User className="w-6 h-6 text-stone-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{conv.other_user_name}</p>
                    <p className="text-sm text-stone-500 truncate">{conv.last_message || 'No messages yet'}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-300" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Message View
        <>
          <div className="flex-1 bg-stone-50 p-6 overflow-y-auto" data-testid="chat-messages">
            <div className="flex flex-col space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-4 rounded-2xl shadow-sm text-sm max-w-[85%] ${
                    msg.sender_type === userType
                      ? 'bg-stone-900 text-white ml-auto rounded-tr-none'
                      : 'bg-white border border-stone-200 rounded-tl-none'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">
                    {renderMessageContent(msg.content, msg.sender_type === userType ? 'underline underline-offset-2 text-white' : 'underline underline-offset-2 text-stone-900')}
                  </p>
                  <span className="text-xs opacity-60 mt-2 block">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 border-t bg-white">
            <div className="flex space-x-2">
              <input
	                  type="text"
	                  placeholder="Type your message..."
	                  value={newMessage}
	                  onChange={(e) => setNewMessage(e.target.value)}
	                  onKeyDown={(e) => {
	                    if (e.key === 'Enter') {
	                      e.preventDefault();
	                      handleSend();
	                    }
	                  }}
	                  className="flex-1 px-4 py-2 bg-stone-100 rounded-xl focus:outline-none text-sm"
	                  data-testid="message-input"
              />
              <button
                onClick={handleSend}
                disabled={isSending || !newMessage.trim()}
                className="bg-stone-900 text-white p-2 w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-50"
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== PHOTOGRAPHER CARD COMPONENT ====================
const PhotographerCard = ({ photographer, onChat }) => {
  const navigate = useNavigate();
  
  const handleCardClick = (e) => {
    // Prevent navigation if clicking on the chat button
    if (e.target.closest('button')) return;
    navigate(`/photographer/${photographer.id}`);
  };

  // Construct proper image URL
  const getImageUrl = (img) => {
    return getOptimizedImageUrl(img, 'card');
  };

  return (
    <div 
      className="artist-card group relative cursor-pointer" 
      data-testid={`photographer-card-${photographer.id}`}
      onClick={handleCardClick}
    >
      <div className="artist-media aspect-[3/4] bg-stone-200 overflow-hidden mb-4 relative">
        <img
          src={getImageUrl(photographer.img)}
          alt={photographer.name}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent opacity-80"></div>
        <div className="card-actions absolute inset-x-0 bottom-0 p-4 opacity-0 translate-y-4 transition-all flex gap-2 group-hover:opacity-100 group-hover:translate-y-0">
          <button
            onClick={(e) => { e.stopPropagation(); onChat(photographer); }}
            className="flex-1 bg-white text-black py-2.5 rounded-lg text-xs font-bold hover:bg-stone-100"
            data-testid={`chat-btn-${photographer.id}`}
          >
            Message
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/photographer/${photographer.id}`); }}
            className="w-11 bg-white/15 border border-white/25 text-white rounded-lg flex items-center justify-center hover:bg-white/25"
            title="View profile"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-lg leading-tight group-hover:text-stone-600 transition truncate">{photographer.name}</h3>
          <p className="artist-meta text-sm mt-1">
            {photographer.location} • {photographer.category}
          </p>
        </div>
        {photographer.hourly_rate ? (
          <span className="artist-rate shrink-0 rounded-full px-3 py-1 text-[11px] font-bold text-stone-700">
            ${photographer.hourly_rate}/hr
          </span>
        ) : null}
      </div>
    </div>
  );
};

// ==================== HOME PAGE ====================
const HomePage = () => {
  const { isAuthenticated, userType } = useAuth();
  const [photographers, setPhotographers] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useFallbackPhotographers, setUseFallbackPhotographers] = useState(false);
  const navigate = useNavigate();
  const dashboardPath = userType === 'photographer' ? '/pro' : '/dashboard';

  const categories = ['all', 'Editorial', 'Wedding', 'Fashion', 'Portrait'];

  const getFallbackPhotographers = useCallback(() => {
    const searchLower = searchValue.trim().toLowerCase();
    return HOMEPAGE_FALLBACK_ARTISTS.filter((artist) => {
      const categoryMatches = activeCategory === 'all' || artist.category === activeCategory;
      const searchMatches = !searchLower ||
        artist.name.toLowerCase().includes(searchLower) ||
        artist.category.toLowerCase().includes(searchLower) ||
        artist.location.toLowerCase().includes(searchLower);

      return categoryMatches && searchMatches;
    });
  }, [activeCategory, searchValue]);

  const loadPhotographers = useCallback(async () => {
    setLoading(true);

    if (useFallbackPhotographers) {
      setPhotographers(getFallbackPhotographers());
      setLoading(false);
      return;
    }

    try {
      const data = await api.getPhotographers(activeCategory, searchValue);
      setUseFallbackPhotographers(false);
      setPhotographers(data);
    } catch (error) {
      setUseFallbackPhotographers(true);
      setPhotographers(getFallbackPhotographers());
    } finally {
      setLoading(false);
    }
  }, [activeCategory, getFallbackPhotographers, searchValue, useFallbackPhotographers]);

  useEffect(() => {
    loadPhotographers();
  }, [loadPhotographers]);

  const handleOpenChat = (photographer) => {
    setSelectedPhotographer(photographer);
    setChatOpen(true);
  };

  const handleToggleChat = () => {
    if (!chatOpen && photographers.length > 0 && !selectedPhotographer) {
      setSelectedPhotographer(null);
    }
    setChatOpen(!chatOpen);
  };

  const resetFilters = () => {
    setSearchValue('');
    setActiveCategory('all');
  };

  const fallbackArtists = HOMEPAGE_FALLBACK_ARTISTS;
  const displayPhotographers = photographers.length > 0 ? photographers : fallbackArtists;
  const featuredPhotographer = displayPhotographers.find(p => p.name === 'Aura Studio') || displayPhotographers[0];
  const secondaryPhotographer = displayPhotographers.find(p => p.name === 'Elena Rossi') || displayPhotographers[1] || displayPhotographers[0];
  const tertiaryPhotographer = displayPhotographers.find(p => p.category === 'Portrait' && p.id !== secondaryPhotographer?.id) || displayPhotographers[2] || displayPhotographers[0];
  const visibleArtistCount = Math.max(displayPhotographers.length, 8);
  const heroImage = getOptimizedImageUrl(featuredPhotographer?.img, 'hero') || fallbackArtists[0].img;

  const navigateToPhotographer = (photographer) => {
    if (photographer?.id && !String(photographer.id).startsWith('fallback')) {
      navigate(`/photographer/${photographer.id}`);
      return;
    }
    document.getElementById('discover')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEnterAura = () => {
    navigate(isAuthenticated ? dashboardPath : '/register');
  };

  return (
    <div className="aura-site text-[#1c1917] min-h-screen overflow-x-hidden">
      <Navbar
        onOpenChat={handleToggleChat}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />

      {/* Hero Section */}
      <section className="aura-hero relative px-5 sm:px-8 overflow-hidden" data-testid="hero-section">
        <div className="aura-orbital-rail" aria-hidden="true">
          <span><Sparkles className="w-4 h-4" /></span>
          <i />
          <p>Discover the extraordinary</p>
        </div>

        <div className="aura-hero-inner max-w-[1500px] mx-auto relative z-10">
          <div className="aura-hero-grid">
            <div className="aura-hero-copy-block">
              <h1 className="aura-hero-title serif">
                <span className="aura-title-line aura-title-main">Capturing</span>
                <span className="aura-title-line aura-title-emphasis">the soul</span>
                <span className="aura-title-line aura-title-main">of the moment.</span>
              </h1>
              
              <p className="aura-hero-copy">
                Aura is where exceptional photographers and discerning clients come together. Discover visionary artists who transform light, emotion, and perspective into timeless visual stories.
              </p>

              <div className="aura-hero-actions">
                <button 
                  onClick={handleEnterAura}
                  className="aura-cta aura-hero-primary"
                  data-testid="start-exploring-btn"
                >
                  Enter Aura
                  <ArrowUp className="w-4 h-4 rotate-45" />
                </button>
                <a href="#discover" className="aura-hero-text-link">
                  View Artists
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <div className="aura-hero-meta" aria-label="Aura marketplace highlights">
                <button onClick={() => setActiveCategory('all')}>
                  <Users className="w-5 h-5" />
                  <span><strong>{visibleArtistCount}</strong>Artists</span>
                </button>
                <button onClick={() => navigate(isAuthenticated ? dashboardPath : '/login')}>
                  <Lock className="w-5 h-5" />
                  <span><strong>Private</strong>Galleries</span>
                </button>
                <button onClick={handleEnterAura}>
                  <Calendar className="w-5 h-5" />
                  <span><strong>Direct</strong>Booking</span>
                </button>
              </div>
            </div>

            <div className="aura-hero-visual">
              <button 
                className="aura-hero-frame group"
                onClick={() => navigateToPhotographer(featuredPhotographer)}
                data-testid="featured-photographer"
              >
                <img 
                  src={heroImage} 
                  alt={featuredPhotographer?.name || 'Aura featured photographer'}
                  className="w-full h-full object-cover transition-all duration-700"
                />
                <span className="aura-studio-mark">
                  <em>Studio</em>
                  <strong>BY AURA</strong>
                </span>
              </button>

              {secondaryPhotographer && (
                <button 
                  className="aura-portrait-inset aura-portrait-inset-top"
                  onClick={() => navigateToPhotographer(secondaryPhotographer)}
                >
                  <img 
                    src={getOptimizedImageUrl(secondaryPhotographer.img, 'card') || fallbackArtists[1].img} 
                    alt={secondaryPhotographer.name}
                  />
                  <span>
                    <strong>{secondaryPhotographer.name}</strong>
                    <small>{secondaryPhotographer.category}</small>
                  </span>
                </button>
              )}

              {tertiaryPhotographer && (
                <button 
                  className="aura-portrait-inset aura-portrait-inset-bottom"
                  onClick={() => navigateToPhotographer(tertiaryPhotographer)}
                >
                  <img 
                    src={getOptimizedImageUrl(tertiaryPhotographer.img, 'card') || fallbackArtists[2].img} 
                    alt={tertiaryPhotographer.name}
                  />
                  <span>
                    <strong>{tertiaryPhotographer.name}</strong>
                    <small>{tertiaryPhotographer.category}</small>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Discover Section */}
      <section id="discover" className="aura-featured-section px-5 sm:px-8" data-testid="discover-section">
        <div className="aura-featured-panel max-w-[1640px] mx-auto">
          <div className="aura-featured-heading">
            <div>
              <h2 className="serif">Featured Artists</h2>
              <p>Browse a refined roster of visual storytellers, then move straight into booking, messaging, and private gallery delivery.</p>
            </div>

            <a href="#discover" onClick={resetFilters} className="aura-featured-all">
              View all artists
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="aura-featured-filters" data-testid="filter-container">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`aura-filter filter-btn px-5 py-2.5 border text-sm font-semibold transition ${
                  activeCategory === cat
                    ? 'aura-filter-active'
                    : ''
                }`}
                data-testid={`filter-${cat}`}
                data-category={cat}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900 mx-auto"></div>
              <p className="mt-4 text-stone-500">Loading photographers...</p>
            </div>
          ) : photographers.length > 0 ? (
            <div className="aura-featured-grid" data-testid="artist-grid">
              {photographers.map((photographer) => (
                <PhotographerCard
                  key={photographer.id}
                  photographer={photographer}
                  onChat={handleOpenChat}
                />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center" data-testid="empty-state">
              <Camera className="w-12 h-12 text-stone-200 mx-auto mb-4" />
              <p className="text-stone-500">No photographers match your search.</p>
              <button
                onClick={resetFilters}
                className="mt-4 text-stone-900 font-bold underline"
                data-testid="reset-filters-btn"
              >
                View all artists
              </button>
            </div>
          )}
        </div>
      </section>

      <section id="journal" className="aura-standard px-5 sm:px-6 py-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.3fr] gap-12 items-start">
          <div>
            <h2 className="serif text-4xl italic leading-tight">A more composed way to book creative work.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              ['01', 'Find the right eye', 'Filter by style, location, and portfolio quality.'],
              ['02', 'Keep the work moving', 'Confirm bookings, contracts, payments, and messages in one portal.'],
              ['03', 'Deliver privately', 'Send finished images through secure client galleries.']
            ].map(([number, title, description]) => (
              <div key={number} className="aura-standard-item pt-5">
                <p className="text-xs text-white/40 font-bold mb-5">{number}</p>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-sm leading-relaxed text-white/60">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ChatDrawer
        isOpen={chatOpen}
        onClose={() => { setChatOpen(false); setSelectedPhotographer(null); }}
        photographer={selectedPhotographer}
      />

      {chatOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[90]"
          onClick={() => setChatOpen(false)}
        />
      )}
    </div>
  );
};

// ==================== PHOTOGRAPHER PROFILE PAGE ====================
const PhotographerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, userType } = useAuth();
  const [photographer, setPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedPortfolioIndex, setSelectedPortfolioIndex] = useState(null);

  const loadPhotographer = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPhotographer(id);
      setPhotographer(data);
    } catch (error) {
      console.error('Failed to load photographer:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPhotographer();
  }, [loadPhotographer]);

  const getImageUrl = (img) => {
    return getOptimizedImageUrl(img, 'thumb');
  };

  const handleContact = () => {
    if (isAuthenticated && userType === 'client') {
      setChatOpen(true);
    } else {
      navigate('/register');
    }
  };

  const portfolioImages = photographer?.portfolio_images || [];
  const selectedPortfolioImage = selectedPortfolioIndex !== null ? portfolioImages[selectedPortfolioIndex] : null;
  const moveLightbox = (direction) => {
    if (portfolioImages.length === 0 || selectedPortfolioIndex === null) return;
    setSelectedPortfolioIndex((currentIndex) => {
      const nextIndex = (currentIndex + direction + portfolioImages.length) % portfolioImages.length;
      return nextIndex;
    });
  };

  if (loading) {
    return (
      <div className="aura-site min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
      </div>
    );
  }

  if (!photographer) {
    return (
      <div className="aura-site min-h-screen">
        <Navbar />
        <div className="pt-32 text-center">
          <Camera className="w-16 h-16 text-stone-200 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Photographer not found</h1>
          <Link to="/" className="text-stone-600 underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="aura-site aura-photographer-profile min-h-screen text-stone-900">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-32 pb-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.88fr_1.45fr] gap-10 lg:gap-16 items-end">
            {/* Profile Image */}
            <div>
              <div className="aura-hero-frame aspect-[3/4] bg-stone-200 overflow-hidden">
                <img 
                  src={getImageUrl(photographer.img)} 
                  alt={photographer.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Profile Info */}
            <div>
              <div className="mb-6">
                <span className="inline-flex px-3 py-1 bg-white/70 border border-stone-200 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-5">
                  {photographer.category}
                </span>
                <h1 className="text-5xl md:text-7xl serif italic leading-none mb-6" data-testid="photographer-name">
                  {photographer.name}
                </h1>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-stone-500 mb-7">
                  <span className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {photographer.location}
                  </span>
                  {photographer.hourly_rate && (
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      ${photographer.hourly_rate}/hr
                    </span>
                  )}
                  {photographer.available && (
                    <span className="flex items-center text-green-600">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Available
                    </span>
                  )}
                </div>
              </div>

              {photographer.bio && (
                <p className="text-stone-600 text-lg leading-relaxed max-w-2xl mb-9">
                  {photographer.bio}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleContact}
                  className="aura-cta px-8 py-4 font-bold flex items-center justify-center space-x-2 transition"
                  data-testid="contact-photographer-btn"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Contact {photographer.name.split(' ')[0]}</span>
                </button>
                <Link
                  to="/"
                  className="aura-soft-button px-8 py-4 font-bold text-center"
                >
                  Back to Discover
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Section */}
      <section className="aura-section-rule py-16 px-6 max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <h2 className="text-3xl serif italic">Portfolio</h2>
            <p className="text-sm text-stone-500 mt-2">Selected work from this photographer.</p>
          </div>
        </div>
        
        {portfolioImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {portfolioImages.map((img, idx) => (
              <button
                type="button"
                key={idx}
                onClick={() => setSelectedPortfolioIndex(idx)}
                className="block group text-left"
                data-testid={`public-portfolio-${idx}`}
              >
                <div className="aspect-square bg-stone-200 rounded-xl overflow-hidden shadow-sm">
                  <img 
                    src={getImageUrl(img)} 
                    alt={`Portfolio ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="premium-panel bg-white p-12 text-center">
            <Images className="w-16 h-16 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-500">No portfolio images yet</p>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="py-10 text-center border-t border-stone-100">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-stone-300">
          Aura Photographer Marketplace
        </div>
      </footer>

      {/* Chat Drawer */}
      <ChatDrawer
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        photographer={photographer}
      />

      {chatOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[90]"
          onClick={() => setChatOpen(false)}
        />
      )}

      {selectedPortfolioImage && (
        <div className="aura-lightbox" role="dialog" aria-modal="true" aria-label="Portfolio image preview">
          <button
            type="button"
            className="aura-lightbox-backdrop"
            aria-label="Close portfolio preview"
            onClick={() => setSelectedPortfolioIndex(null)}
          />
          <div className="aura-lightbox-panel">
            <img
              src={getOptimizedImageUrl(selectedPortfolioImage, 'full')}
              alt={`Portfolio ${selectedPortfolioIndex + 1}`}
            />
            <div className="aura-lightbox-actions">
              <button type="button" onClick={() => moveLightbox(-1)} aria-label="Previous image">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span>{selectedPortfolioIndex + 1} / {portfolioImages.length}</span>
              <button type="button" onClick={() => moveLightbox(1)} aria-label="Next image">
                <ChevronRight className="w-5 h-5" />
              </button>
              <a href={getOptimizedImageUrl(selectedPortfolioImage, 'full')} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-5 h-5" />
              </a>
              <button type="button" onClick={() => setSelectedPortfolioIndex(null)} aria-label="Close image preview">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== LOGIN PAGE ====================
// ==================== UNIFIED AUTH PAGE (Login + Register) ====================
const AuthPage = ({ initialMode = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [isClient, setIsClient] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    category: 'Portrait',
    location: '',
    bio: '',
    img: '',
    hourly_rate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const categories = ['Editorial', 'Wedding', 'Fashion', 'Portrait'];

  const getPostAuthDestination = (nextUserType) => {
    const from = location.state?.from;
    if (from?.pathname) {
      const requestedPath = `${from.pathname}${from.search || ''}${from.hash || ''}`;
      if (from.pathname.startsWith('/pro') && nextUserType !== 'photographer') return '/dashboard';
      if (from.pathname.startsWith('/dashboard') && nextUserType === 'photographer') return '/pro';
      if (from.pathname.startsWith('/admin') && nextUserType !== 'photographer') return '/dashboard';
      return requestedPath;
    }
    return nextUserType === 'photographer' ? '/pro' : '/dashboard';
  };

  // Reset form when switching modes
  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      category: 'Portrait',
      location: '',
      bio: '',
      img: '',
      hourly_rate: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const response = isClient 
          ? await api.loginClient({ email: formData.email, password: formData.password })
          : await api.loginPhotographer({ email: formData.email, password: formData.password });
        login(response);
        navigate(getPostAuthDestination(response.user_type), { replace: true });
      } else {
        // Register mode
        if (isClient) {
          const response = await api.registerClient({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            phone: formData.phone || null
          });
          login(response);
          navigate(getPostAuthDestination(response.user_type), { replace: true });
        } else {
          const response = await api.registerPhotographer({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            category: formData.category,
            location: formData.location,
            bio: formData.bio || null,
            img: formData.img,
            hourly_rate: parseFloat(formData.hourly_rate) || 0,
            portfolio_images: [],
            available: true
          });
          login(response);
          navigate(getPostAuthDestination(response.user_type), { replace: true });
        }
      }
    } catch (err) {
      const serverTimeout = err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout');
      setError(
        err.response?.data?.detail ||
        (serverTimeout
          ? 'Aura servers are taking too long to respond. Please try again.'
          : (mode === 'login' ? 'Invalid email or password' : 'Registration failed. Please try again.'))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aura-auth-page aura-site text-stone-900 overflow-hidden">
      <div className="flex min-h-screen">
        
        {/* Left Side: Visual/Brand (Hidden on Mobile) */}
        <div 
          className="aura-auth-visual hidden lg:flex lg:w-1/2 relative items-center justify-center p-16 xl:p-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1493863641943-9b68992a8d07?auto=format&fit=crop&q=80&w=1200')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-stone-950/35 backdrop-brightness-90"></div>
          
          <div className="aura-auth-panel relative z-10 text-white max-w-lg py-10">
            <Link to="/" className="text-3xl font-bold serif italic mb-12 inline-block">AURA</Link>
            <h1 className="text-6xl serif leading-tight mb-6">The art of <br/><span className="italic">seeing</span> differently.</h1>
            <p className="text-stone-200 text-lg font-light leading-relaxed">
              Join a refined workspace for finding photographers, managing bookings, and delivering private client galleries.
            </p>
            
            <div className="mt-12 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-2xl serif italic">01</p>
                <p className="text-white/60">Discover</p>
              </div>
              <div>
                <p className="text-2xl serif italic">02</p>
                <p className="text-white/60">Book</p>
              </div>
              <div>
                <p className="text-2xl serif italic">03</p>
                <p className="text-white/60">Deliver</p>
              </div>
            </div>
          </div>

          {/* Footer Decorative */}
          <div className="absolute bottom-10 left-10 text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold">
            EST. MMXXIV • PARIS / NYC / TOKYO
          </div>
        </div>

        {/* Right Side: Auth Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 md:p-16">
          <div className="w-full max-w-md space-y-8">
            
            {/* Header */}
            <div className="text-center lg:text-left">
              <Link to="/" className="lg:hidden text-2xl font-bold serif italic mb-8 block">AURA</Link>
              <h2 className="text-4xl serif italic mb-2">
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-stone-500 text-sm">
                {mode === 'login' 
                  ? 'Enter your credentials to access your portal.'
                  : 'Join our community of creative professionals.'}
              </p>
            </div>

            {/* Client/Photographer Toggle */}
            <div className="flex bg-white/70 border border-stone-200 rounded-full p-1">
              <button
                onClick={() => setIsClient(true)}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium transition ${isClient ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500'}`}
                data-testid="client-tab"
              >
                {mode === 'register' ? 'I need a photographer' : 'Client'}
              </button>
              <button
                onClick={() => setIsClient(false)}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium transition ${!isClient ? 'bg-stone-900 text-white shadow-sm' : 'text-stone-500'}`}
                data-testid="photographer-tab"
              >
                {mode === 'register' ? 'I am a photographer' : 'Photographer'}
              </button>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            {/* Form */}
            <form className="space-y-5" onSubmit={handleSubmit} data-testid={mode === 'login' ? 'login-form' : 'register-form'}>
              
              {/* Name field - only for registration */}
              {mode === 'register' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">
                    {isClient ? 'Full Name' : 'Studio / Full Name'}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">
                      <User size={16} />
                    </div>
                    <input 
                      type="text" 
                      required 
                      placeholder={isClient ? "Your full name" : "Studio or your name"}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="aura-input pl-11 pr-4 py-3.5 text-sm"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              {/* Email field */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Email Address</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  </div>
                  <input 
                    type="email" 
                    required 
                    placeholder="name@studio.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="aura-input pl-11 pr-4 py-3.5 text-sm"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Password</label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => setError('Password reset is not configured yet. Please contact support for account access.')}
                      className="text-[10px] font-bold uppercase tracking-widest text-stone-400 hover:text-stone-900 transition"
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </div>
                  <input 
                    type="password" 
                    required 
                    minLength={mode === 'register' ? 6 : undefined}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="aura-input pl-11 pr-4 py-3.5 text-sm"
                    data-testid="password-input"
                  />
                </div>
              </div>

              {/* Registration-only fields */}
              {mode === 'register' && (
                <>
                  {isClient ? (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Phone (Optional)</label>
                      <input
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="aura-input px-4 py-3.5 text-sm"
                        data-testid="phone-input"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Category</label>
                          <select
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="aura-input px-4 py-3.5 text-sm"
                            data-testid="category-select"
                          >
                            {categories.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Hourly Rate</label>
                          <input
                            type="number"
                            placeholder="$150"
                            value={formData.hourly_rate}
                            onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                            className="aura-input px-4 py-3.5 text-sm"
                            data-testid="rate-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Location</label>
                        <input
                          type="text"
                          placeholder="New York, NY"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          required
                          className="aura-input px-4 py-3.5 text-sm"
                          data-testid="location-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Profile Image URL</label>
                        <input
                          type="url"
                          placeholder="https://..."
                          value={formData.img}
                          onChange={(e) => setFormData({ ...formData, img: e.target.value })}
                          required
                          className="aura-input px-4 py-3.5 text-sm"
                          data-testid="img-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Bio (Optional)</label>
                        <textarea
                          placeholder="Tell us about yourself and your photography style..."
                          value={formData.bio}
                          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                          rows={2}
                          className="aura-input px-4 py-3.5 text-sm resize-none"
                          data-testid="bio-input"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Remember me - only for login */}
              {mode === 'login' && (
                <div className="flex items-center space-x-3 px-1">
                  <input 
                    type="checkbox" 
                    id="remember" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300 accent-stone-900"
                  />
                  <label htmlFor="remember" className="text-xs text-stone-500 font-medium">Remember me for 30 days</label>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                className="aura-cta w-full py-4 font-bold text-sm transition active:scale-[0.98] disabled:opacity-50"
                data-testid={mode === 'login' ? 'login-btn' : 'register-btn'}
              >
                {loading 
                  ? (mode === 'login' ? 'Signing in...' : 'Creating account...') 
                  : (mode === 'login' ? 'Sign In to Aura' : 'Create Account')}
              </button>
            </form>

            {/* Social Login */}
            <div className="space-y-5">
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200"></div></div>
                <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-widest">
                  <span className="bg-[#f7f5f0] px-4 text-stone-400">Or continue with</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button 
                  type="button" 
                  className="aura-soft-button flex items-center justify-center space-x-2 py-3 opacity-50 cursor-not-allowed"
                  disabled
                  title="Google sign-in is not configured yet"
                  data-testid="google-auth-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  <span className="text-xs font-bold">Google</span>
                </button>
                <button type="button" className="aura-soft-button flex items-center justify-center space-x-2 py-3 opacity-50 cursor-not-allowed" disabled>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  <span className="text-xs font-bold">Apple</span>
                </button>
              </div>
            </div>

            {/* Footer toggle */}
            <p className="text-center text-sm text-stone-500">
              {mode === 'login' ? (
                <>Not a member yet? <button onClick={() => switchMode('register')} className="text-stone-900 font-bold hover:underline">Create Account</button></>
              ) : (
                <>Already have an account? <button onClick={() => switchMode('login')} className="text-stone-900 font-bold hover:underline">Sign In</button></>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Aliases for backward compatibility with routes
const LoginPage = () => <AuthPage initialMode="login" />;
const RegisterPage = () => <AuthPage initialMode="register" />;

// ==================== BOOKING MODAL COMPONENT ====================
const BookingModal = ({ photographers, selectedPhotographer, activeBooking, token, onClose, onBookingCreated, onBookingComplete }) => {
  const [step, setStep] = useState(activeBooking ? getStepFromStatus(activeBooking.status) : 'select');
  const [photographer, setPhotographer] = useState(selectedPhotographer);
  const [availability, setAvailability] = useState([]);
  const [sessionPrices, setSessionPrices] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [sessionType, setSessionType] = useState('half-day');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(activeBooking);
  const [signature, setSignature] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  function getStepFromStatus(status) {
    switch (status) {
      case 'pending': return 'pending';
      case 'pendingSignature': return 'contract';
      case 'pendingContract': return 'contract';
      case 'approved': return 'contract';
      case 'payable': return 'payment';
      case 'signed': return 'payment';
      case 'confirmed': return 'success';
      case 'paid': return 'success';
      default: return 'select';
    }
  }

  const loadAvailability = useCallback(async () => {
    if (!photographer) return;
    try {
      const data = await api.getPhotographerAvailability(photographer.id, currentMonth, currentYear);
      setAvailability(data.available_dates || []);
      setSessionPrices(data.session_prices || {});
    } catch (error) {
      console.error('Failed to load availability:', error);
    }
  }, [photographer, currentMonth, currentYear]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const isDateAvailable = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return availability.includes(dateStr);
  };

  const handleSelectDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  const handleRequestBooking = async () => {
    if (!selectedDate || !photographer) return;
    setLoading(true);
    try {
      const newBooking = await api.createBooking({
        photographer_id: photographer.id,
        date: selectedDate,
        session_type: sessionType,
        notes: notes
      }, token);
      setBooking(newBooking);
      onBookingCreated(newBooking);
      setStep('pending');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async () => {
    if (!signature || !agreeTerms || !booking) return;
    setLoading(true);
    try {
      if (booking.contract_id || booking.contract?.contractId) {
        await api.signContract(booking.contract_id || booking.contract.contractId, signature, token);
      } else {
        await api.signBooking(booking.id, signature, token);
      }
      setBooking({
        ...booking,
        status: 'signed',
        contract_signed: true,
        invoice_status: booking.invoice ? 'payable' : booking.invoice_status,
        invoice: booking.invoice ? { ...booking.invoice, status: 'payable', isPayable: true } : booking.invoice
      });
      setStep('payment');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setLoading(false);
    }
  };

  const handlePayDeposit = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      if (booking.invoice_id || booking.invoice?.invoiceId) {
        await api.payInvoice(booking.invoice_id || booking.invoice.invoiceId, booking.id, token);
      } else {
        await api.payBookingDeposit(booking.id, token);
      }
      setBooking({ ...booking, status: 'confirmed', deposit_paid: true, invoice_status: 'paid' });
      setStep('success');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const invoice = booking?.invoice;
  const photographerAmount = invoice?.photographerAmount ?? booking?.photographer_amount ?? booking?.total_amount ?? 0;
  const auraFee = invoice?.auraFee ?? booking?.aura_fee ?? 0;
  const totalDue = invoice?.totalDue ?? booking?.total_due ?? booking?.deposit_amount ?? 0;

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-4"></div>);
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isAvailable = isDateAvailable(day);
      const isSelected = selectedDate === dateStr;
      const isPast = new Date(dateStr) < new Date(new Date().toDateString());

      days.push(
        <button
          key={day}
          onClick={() => isAvailable && !isPast && handleSelectDate(day)}
          disabled={!isAvailable || isPast}
          className={`p-4 rounded-xl text-xs flex items-center justify-center transition ${
            isSelected 
              ? 'bg-stone-900 text-white' 
              : isAvailable && !isPast
                ? 'bg-stone-100 hover:bg-stone-900 hover:text-white border border-stone-200'
                : 'text-stone-300 cursor-not-allowed'
          }`}
          data-testid={`calendar-day-${day}`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg serif italic">
              {step === 'select' && 'New Booking'}
              {step === 'pending' && 'Request Sent'}
              {step === 'contract' && 'Review & Sign Contract'}
              {step === 'payment' && 'Secure Your Booking'}
              {step === 'success' && 'Booking Confirmed!'}
            </h3>
            {photographer && step !== 'success' && (
              <p className="text-sm text-stone-500">with {photographer.name}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full" data-testid="close-booking-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Step: Select Photographer & Date */}
          {step === 'select' && (
            <div className="space-y-6">
              {!photographer ? (
                <>
                  <p className="text-sm text-stone-500 mb-4">Select a photographer to book</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {photographers.map(p => (
                      <button
                        key={p.id}
                        onClick={() => setPhotographer(p)}
                        className="w-full flex items-center space-x-4 p-4 border border-stone-200 rounded-2xl hover:border-stone-400 transition text-left"
                        data-testid={`select-photographer-${p.id}`}
                      >
                        <img src={p.img} alt={p.name} className="w-12 h-12 rounded-xl object-cover" />
                        <div>
                          <div className="font-bold text-sm">{p.name}</div>
                          <div className="text-xs text-stone-500">{p.category} • {p.location}</div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-300 ml-auto" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => setPhotographer(null)} className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" /> Change photographer
                    </button>
                    <div className="text-right">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400">
                        {monthNames[currentMonth - 1]} {currentYear}
                      </span>
                    </div>
                  </div>

                  {/* Month Navigation */}
                  <div className="flex justify-between items-center mb-4">
                    <button 
                      onClick={() => {
                        if (currentMonth === 1) {
                          setCurrentMonth(12);
                          setCurrentYear(currentYear - 1);
                        } else {
                          setCurrentMonth(currentMonth - 1);
                        }
                      }}
                      className="p-2 hover:bg-stone-100 rounded-lg"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="font-medium">{monthNames[currentMonth - 1]} {currentYear}</span>
                    <button 
                      onClick={() => {
                        if (currentMonth === 12) {
                          setCurrentMonth(1);
                          setCurrentYear(currentYear + 1);
                        } else {
                          setCurrentMonth(currentMonth + 1);
                        }
                      }}
                      className="p-2 hover:bg-stone-100 rounded-lg"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Calendar */}
                  <div className="grid grid-cols-7 gap-2 mb-6">
                    {dayNames.map((day, i) => (
                      <div key={i} className="text-center text-[10px] font-bold text-stone-400 uppercase p-2">{day}</div>
                    ))}
                    {renderCalendar()}
                  </div>

                  {availability.length === 0 && (
                    <p className="text-center text-stone-400 text-sm py-4">No available dates this month</p>
                  )}

                  {/* Session Type Selection */}
                  {selectedDate && (
                    <div className="space-y-4 border-t pt-6">
                      <label className="text-xs font-bold uppercase tracking-widest text-stone-400">Session Type</label>
                      <div className="grid grid-cols-3 gap-3">
                        {Object.entries(sessionPrices).map(([type, info]) => (
                          <button
                            key={type}
                            onClick={() => setSessionType(type)}
                            className={`p-4 rounded-xl border-2 text-left transition ${
                              sessionType === type ? 'border-stone-900 bg-stone-50' : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            <div className="text-xs font-bold capitalize">{type.replace('-', ' ')}</div>
                            <div className="text-lg font-bold serif">${info.total}</div>
                            <div className="text-[10px] text-stone-500">{info.hours} hours</div>
                          </button>
                        ))}
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-stone-400 block mb-2">Notes (optional)</label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Any special requests..."
                          className="w-full p-3 border border-stone-200 rounded-xl text-sm resize-none"
                          rows={2}
                        />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleRequestBooking}
                    disabled={!selectedDate || loading}
                    className={`w-full py-4 rounded-2xl font-bold mt-6 transition ${
                      selectedDate && !loading
                        ? 'bg-stone-900 text-white hover:bg-stone-800'
                        : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    }`}
                    data-testid="request-booking-btn"
                  >
                    {loading ? 'Creating Booking...' : `Request ${selectedDate ? new Date(selectedDate).toLocaleDateString() : 'Selected Date'}`}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step: Pending Approval */}
          {step === 'pending' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-stone-400 animate-pulse" />
              </div>
              <h3 className="text-2xl font-bold serif mb-2">Request Sent</h3>
              <p className="text-stone-500 text-sm max-w-xs mx-auto mb-8">
                {booking?.photographer_name} has been notified. They usually respond within 2 hours.
              </p>
              <div className="bg-stone-50 rounded-2xl p-4 text-left mb-6">
                <div className="text-xs text-stone-500 mb-1">Requested Date</div>
                <div className="font-bold">{booking?.date ? new Date(booking.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : ''}</div>
              </div>
              <button 
                onClick={onBookingComplete}
                className="bg-stone-900 text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-stone-800 transition"
              >
                Back to Dashboard
              </button>
            </div>
          )}

          {/* Step: Contract */}
          {step === 'contract' && booking && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full">Booking Approved</span>
              </div>
              
              <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200 h-48 overflow-y-auto text-xs leading-relaxed text-stone-600 font-mono">
                <h4 className="font-bold text-stone-900 mb-4 uppercase">Photography Service Agreement</h4>
                This contract is between {booking.client_name} (Client) and {booking.photographer_name} (Artist).<br/><br/>
                1. SERVICES: Artist agrees to provide {booking.hours || 4} hours of photography on {new Date(booking.date).toLocaleDateString()}.<br/><br/>
                2. PAYMENT: Photographer fee is ${Number(photographerAmount || 0).toFixed(2)}. Aura platform fee is ${Number(auraFee || 0).toFixed(2)}. Total due is ${Number(totalDue || 0).toFixed(2)} and is only payable after this contract is signed.<br/><br/>
                3. DELIVERY: Digital gallery will be delivered within 14 days of the session.<br/><br/>
                4. USAGE: Personal usage rights are granted. Commercial rights require additional licensing.
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input 
                    type="checkbox" 
                    id="terms-agree" 
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300"
                  />
                  <label htmlFor="terms-agree" className="text-xs text-stone-500">I agree to the terms and conditions</label>
                </div>
                <div className="p-4 border-2 border-dashed border-stone-200 rounded-2xl">
                  <input 
                    type="text" 
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Type your full name to E-Sign" 
                    className="w-full bg-transparent focus:outline-none text-sm font-bold serif italic"
                    data-testid="signature-input"
                  />
                </div>
                <button 
                  onClick={handleSignContract}
                  disabled={!signature || !agreeTerms || loading}
                  className={`w-full py-4 rounded-2xl font-bold transition ${
                    signature && agreeTerms && !loading
                      ? 'bg-stone-900 text-white hover:bg-stone-800'
                      : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                  }`}
                  data-testid="sign-contract-btn"
                >
                  {loading ? 'Signing...' : 'Sign & Proceed to Payment'}
                </button>
              </div>
            </div>
          )}

          {/* Step: Payment */}
          {step === 'payment' && booking && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <p className="text-xs text-stone-500">Contract signed. Invoice is now payable.</p>
              </div>
              
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-stone-500">Photographer Fee</span>
                  <span className="text-sm font-bold">${Number(photographerAmount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-stone-500">Aura Platform Fee (10%)</span>
                  <span className="text-sm font-bold">${Number(auraFee || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-stone-200">
                  <span className="text-stone-900 font-bold">Total Due Now</span>
                  <span className="text-2xl font-bold serif">${Number(totalDue || 0).toFixed(2)}</span>
                </div>
              </div>

              <button 
                onClick={handlePayDeposit}
                disabled={loading}
                className="w-full bg-stone-900 text-white py-4 rounded-2xl font-bold shadow-xl shadow-stone-200 hover:bg-stone-800 transition disabled:opacity-50"
                data-testid="pay-deposit-btn"
              >
                {loading ? 'Processing...' : `Confirm $${Number(totalDue || 0).toFixed(2)} Invoice`}
              </button>
              <p className="text-[10px] text-center text-stone-400 uppercase font-bold tracking-widest">
                Secure escrow payment
              </p>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold serif mb-2">Booking Confirmed!</h3>
              <p className="text-stone-500 text-sm mb-8">
                You're all set. {booking?.photographer_name} has been notified and the date is locked.
              </p>
              <button 
                onClick={onBookingComplete}
                className="bg-stone-900 text-white px-8 py-3 rounded-xl text-sm font-bold hover:bg-stone-800 transition"
                data-testid="booking-done-btn"
              >
                Back to Dashboard
              </button>
            </div>
          )}
        </div>

        {/* Sidebar Progress */}
        {step !== 'success' && (
          <div className="p-6 bg-stone-50 border-t">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-stone-400">
              <span className={step === 'select' ? 'text-stone-900' : ''}>1. Select</span>
              <span className={step === 'pending' ? 'text-stone-900' : ''}>2. Approval</span>
              <span className={step === 'contract' ? 'text-stone-900' : ''}>3. Sign</span>
              <span className={step === 'payment' ? 'text-stone-900' : ''}>4. Pay</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CLIENT HUB DASHBOARD ====================
const DashboardPage = () => {
  const { user, userType, token, isAuthenticated, logout } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [activeBooking, setActiveBooking] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState([]);
  const navigate = useNavigate();

  // Generate notifications from current data
  const getNotifications = () => {
    const notifications = [];
    
    // New messages
    conversations.forEach(conv => {
      if (conv.last_message) {
        notifications.push({
          id: `msg-${conv.id}-${conv.last_message_at || conv.last_message}`,
          type: 'message',
          title: 'New Message',
          description: `${conv.other_user_name || conv.photographer_name}: ${conv.last_message.slice(0, 40)}...`,
          time: 'Recently',
          icon: 'message',
          action: () => openChat(conv)
        });
      }
    });

    // Booking updates
    bookings.forEach(booking => {
      if (booking.status === 'approved') {
        notifications.push({
          id: `booking-${booking.id}`,
          type: 'booking',
          title: 'Booking Approved',
          description: `Your session on ${booking.booking_date} has been approved`,
          time: 'Recently',
          icon: 'calendar'
        });
      }
    });

    // Delivered images
    jobs.forEach(job => {
      if (job.delivered_images && job.delivered_images.length > 0) {
        notifications.push({
          id: `gallery-${job.id}`,
          type: 'gallery',
          title: 'Images Delivered',
          description: `${job.delivered_images.length} new images from ${job.photographer_name}`,
          time: 'Recently',
          icon: 'image',
          action: () => { setSelectedJob(job); setShowGallery(true); }
        });
      }
    });

    return notifications
      .filter((notification) => !dismissedNotificationIds.includes(notification.id))
      .slice(0, 10);
  };

  const markNotificationsRead = () => {
    const visibleNotificationIds = getNotifications().map((notification) => notification.id);
    setDismissedNotificationIds((currentIds) => [...new Set([...currentIds, ...visibleNotificationIds])]);
    setShowNotifications(false);
  };

  const loadMessages = async (conversationId) => {
    try {
      const msgs = await api.getMessages(conversationId, token);
      setMessages(msgs);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const openChat = (conv) => {
    setSelectedConversation(conv);
    setShowNotifications(false);
    loadMessages(conv.id);
  };

  const closeChat = () => {
    setSelectedConversation(null);
    setMessages([]);
    setNewMessage('');
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !token) return;
    const outgoingMessage = newMessage.trim();

    setSendingMessage(true);
    try {
      const message = await api.sendMessage({
        photographer_id: selectedConversation.photographer_id,
        conversation_id: selectedConversation.id,
        content: outgoingMessage
      }, token);
      setMessages((currentMessages) => [...currentMessages, message]);
      setNewMessage('');
      // Refresh conversations to update last_message
      const convs = await api.getConversations(token);
      setConversations(convs);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [convs, jobsList, photographersList, bookingsList] = await Promise.all([
        api.getConversations(token),
        api.getJobs(token),
        api.getPhotographers(),
        api.getBookings(token)
      ]);
      setConversations(convs);
      setJobs(jobsList);
      setPhotographers(photographersList);
      setBookings(bookingsList);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (userType === 'photographer') {
      navigate('/pro');
      return;
    }
    loadData();
  }, [isAuthenticated, userType, navigate, loadData]);

  const getProgressStage = (status) => {
    switch (status) {
      case 'pending': return 0;
      case 'pendingSignature': return 1;
      case 'approved': return 1;
      case 'signed': return 1;
      case 'confirmed': return 1;
      case 'in_progress': return 2;
      case 'approvedForFinalDelivery': return 2;
      case 'completed': return 3;
      default: return 0;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Awaiting Confirmation';
      case 'pendingSignature': return 'Contract Pending';
      case 'approved': return 'Contract Pending';
      case 'signed': return 'Invoice Payable';
      case 'confirmed': return 'Confirmed';
      case 'in_progress': return 'In Progress';
      case 'approvedForFinalDelivery': return 'Final Sign-Off Complete';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const signatureBooking = bookings.find((booking) => ['pendingSignature', 'approved'].includes(booking.status) || booking.invoice_status === 'pendingContract');
  const payableBooking = bookings.find((booking) => (booking.status === 'signed' || booking.invoice_status === 'payable') && !booking.deposit_paid);
  const actionBooking = signatureBooking || payableBooking;

  const calculateBalance = () => {
    return bookings
      .filter((booking) => ['pendingSignature', 'approved', 'signed'].includes(booking.status) || ['pendingContract', 'payable'].includes(booking.invoice_status))
      .reduce((total, booking) => total + (booking.total_due || booking.deposit_amount || 0), 0);
  };

  const handlePayBalance = () => {
    if (!actionBooking) return;
    setActiveBooking(actionBooking);
    setShowBookingModal(true);
  };

  // Get active project (most recent non-completed job)
  const activeProject = jobs.find(j => j.status !== 'completed' && j.status !== 'cancelled');
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const clientBalance = calculateBalance();
  const activeProjectsCount = jobs.filter(j => j.status !== 'completed' && j.status !== 'cancelled').length;
  const totalImagesReceived = jobs.reduce((acc, j) => acc + (j.delivered_images?.length || 0), 0);
  const openMessageCount = conversations.filter(conv => conv.last_message).length;
  const nextBooking = bookings.find((booking) => ['approved', 'pendingSignature', 'signed', 'confirmed', 'paid'].includes(booking.status));
  const nextClientAction = signatureBooking
    ? 'Sign contract'
    : payableBooking
      ? 'Pay invoice'
      : activeProject?.delivered_images?.length > 0
        ? 'Review gallery'
        : 'Book a session';

  // Get random recommended photographer
  const recommendedPhotographer = photographers.find(p => 
    !jobs.some(j => j.photographer_id === p.id)
  ) || photographers[0];

  if (!isAuthenticated) return null;

  return (
    <div className="aura-client-portal aura-client-suite aura-site min-h-screen text-stone-900">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 aura-portal-nav">
        <div className="max-w-7xl mx-auto px-6 h-[76px] flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="aura-logo text-xl font-bold serif italic">AURA</Link>
            <span className="h-4 w-px bg-stone-200"></span>
            <span className="text-xs font-bold uppercase tracking-widest text-stone-400">Client Hub</span>
          </div>
          
          <div className="flex items-center space-x-6">
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative text-stone-400 hover:text-stone-900 transition p-2 rounded-full hover:bg-stone-100"
                data-testid="notification-bell-client"
              >
                <Bell className="w-5 h-5" />
                {getNotifications().length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="premium-panel absolute right-0 mt-2 w-80 bg-white overflow-hidden z-50">
                  <div className="p-4 border-b border-stone-100 flex justify-between items-center">
                    <h3 className="font-bold">Notifications</h3>
                    <span className="text-xs text-stone-400">{getNotifications().length} new</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {getNotifications().length === 0 ? (
                      <div className="p-8 text-center text-stone-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 text-stone-200" />
                        <p className="text-sm">No new notifications</p>
                      </div>
                    ) : (
                      getNotifications().map((notif) => (
                        <div 
                          key={notif.id}
                          onClick={() => {
                            setDismissedNotificationIds((currentIds) => [...new Set([...currentIds, notif.id])]);
                            if (notif.action) notif.action();
                            setShowNotifications(false);
                          }}
                          className="p-4 hover:bg-stone-50 cursor-pointer border-b border-stone-50 last:border-0"
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notif.type === 'message' ? 'bg-blue-100 text-blue-600' :
                              notif.type === 'booking' ? 'bg-green-100 text-green-600' :
                              'bg-purple-100 text-purple-600'
                            }`}>
                              {notif.type === 'message' && <MessageSquare className="w-4 h-4" />}
                              {notif.type === 'booking' && <Calendar className="w-4 h-4" />}
                              {notif.type === 'gallery' && <Images className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{notif.title}</p>
                              <p className="text-xs text-stone-500 truncate">{notif.description}</p>
                              <p className="text-xs text-stone-400 mt-1">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {getNotifications().length > 0 && (
                    <div className="p-3 border-t border-stone-100 text-center">
                      <button
                        type="button"
                        onClick={markNotificationsRead}
                        className="text-xs text-stone-500 hover:text-stone-900 font-medium"
                      >
                        Mark all as read
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3 pl-4 border-l border-stone-200">
              <div className="text-right hidden sm:block">
                <div className="text-xs font-bold">{user?.name}</div>
                <div className="text-[10px] text-stone-400">Premium Member</div>
              </div>
              <div className="relative group">
                <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center cursor-pointer">
                  <User className="w-4 h-4 text-stone-500" />
                </div>
                <div className="premium-panel absolute right-0 mt-2 w-48 bg-white py-2 hidden group-hover:block">
                  <Link to="/" className="block px-4 py-2 text-sm hover:bg-stone-50">Browse Photographers</Link>
                  <button 
                    onClick={() => { logout(); navigate('/'); }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-stone-50"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <header className="mb-12 flex flex-col md:flex-row md:justify-between md:items-end gap-5" data-testid="client-hub-header">
          <div>
            <h1 className="text-4xl md:text-5xl serif italic mb-2">My Collections</h1>
            <p className="text-stone-500">Manage your bookings, galleries, and collaboration with artists.</p>
          </div>
          <button 
            onClick={() => setShowBookingModal(true)}
            className="aura-cta px-6 py-3 text-sm font-bold flex items-center justify-center space-x-2 transition"
            data-testid="new-booking-btn"
          >
            <CalendarPlus className="w-4 h-4" />
            <span>New Booking</span>
          </button>
        </header>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
          </div>
        ) : (
          <>
            <section className="aura-client-stats" data-testid="client-dashboard-stats">
              <article className="aura-client-stat-card">
                <span className="aura-client-stat-icon"><Camera className="w-5 h-5" /></span>
                <div>
                  <p>Active Projects</p>
                  <strong className="serif">{activeProjectsCount}</strong>
                  <small>{activeProject ? getStatusLabel(activeProject.status) : 'Ready to book'}</small>
                </div>
              </article>

              <article className="aura-client-stat-card">
                <span className="aura-client-stat-icon"><Wallet className="w-5 h-5" /></span>
                <div>
                  <p>Balance Due</p>
                  <strong className="serif">${clientBalance.toLocaleString()}</strong>
                  <small>{nextClientAction}</small>
                </div>
              </article>

              <article className="aura-client-stat-card">
                <span className="aura-client-stat-icon"><Images className="w-5 h-5" /></span>
                <div>
                  <p>Images Received</p>
                  <strong className="serif">{totalImagesReceived}</strong>
                  <small>{completedJobs.length} completed collection{completedJobs.length === 1 ? '' : 's'}</small>
                </div>
              </article>

              <article className="aura-client-stat-card">
                <span className="aura-client-stat-icon"><MessageSquare className="w-5 h-5" /></span>
                <div>
                  <p>Conversations</p>
                  <strong className="serif">{openMessageCount}</strong>
                  <small>{nextBooking ? `Next booking: ${nextBooking.booking_date || nextBooking.date || 'scheduled'}` : 'No booking scheduled'}</small>
                </div>
              </article>
            </section>

            <div className="aura-client-dashboard-grid grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Active Bookings & Galleries */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Active Project Card */}
              {activeProject ? (
                <div className="aura-client-project-card client-card bg-white rounded-[2rem] border border-stone-200 overflow-hidden hover:-translate-y-1 transition-transform" data-testid="active-project">
                  <div className="p-8">
                    <div className="flex flex-col md:flex-row justify-between md:items-start mb-6 gap-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 rounded-2xl bg-stone-900 flex items-center justify-center text-white overflow-hidden">
                          {activeProject.photographer_img ? (
                            <img src={activeProject.photographer_img} alt="" className="w-full h-full object-cover opacity-60" />
                          ) : (
                            <Camera className="w-6 h-6" />
                          )}
                        </div>
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Current Project</span>
                          <h2 className="text-xl font-bold serif">{activeProject.title}</h2>
                          <p className="text-sm text-stone-500 italic serif">with {activeProject.photographer_name}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="bg-stone-100 text-stone-900 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2">
                          {getStatusLabel(activeProject.status)}
                        </span>
                        {activeProject.scheduled_date && (
                          <div className="text-[10px] text-stone-400 font-bold uppercase">
                            Scheduled: {activeProject.scheduled_date}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-8">
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">
                        <span className={getProgressStage(activeProject.status) >= 0 ? 'text-stone-900' : ''}>Booked</span>
                        <span className={getProgressStage(activeProject.status) >= 1 ? 'text-stone-900' : ''}>Confirmed</span>
                        <span className={getProgressStage(activeProject.status) >= 2 ? 'text-stone-900' : ''}>In Progress</span>
                        <span className={getProgressStage(activeProject.status) >= 3 ? 'text-stone-900' : ''}>Delivered</span>
                      </div>
                      <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden flex">
                        <div className={`w-1/4 ${getProgressStage(activeProject.status) >= 0 ? 'bg-stone-900' : 'bg-stone-100'} border-r border-white`}></div>
                        <div className={`w-1/4 ${getProgressStage(activeProject.status) >= 1 ? 'bg-stone-900' : 'bg-stone-100'} border-r border-white`}></div>
                        <div className={`w-1/4 ${getProgressStage(activeProject.status) >= 2 ? 'bg-stone-900' : 'bg-stone-100'} border-r border-white`}></div>
                        <div className={`w-1/4 ${getProgressStage(activeProject.status) >= 3 ? 'bg-stone-900' : 'bg-stone-100'}`}></div>
                      </div>
                    </div>

                    {activeProject.description && (
                      <p className="text-stone-600 text-sm mb-6">{activeProject.description}</p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {activeProject.delivered_images && activeProject.delivered_images.length > 0 ? (
                        <Link 
                          to={`/gallery/${activeProject.id}`}
                          className="bg-stone-900 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center space-x-2 hover:bg-stone-700 transition"
                          data-testid="view-gallery-btn"
                        >
                          <Images className="w-4 h-4" />
                          <span>View Gallery ({activeProject.delivered_images.length})</span>
                        </Link>
                      ) : (
                        <div className="bg-stone-100 text-stone-400 py-3 rounded-xl text-sm font-bold flex items-center justify-center space-x-2">
                          <Images className="w-4 h-4" />
                          <span>Gallery Pending</span>
                        </div>
                      )}
                      <Link 
                        to="/"
                        className="border border-stone-200 text-stone-900 py-3 rounded-xl text-sm font-bold flex items-center justify-center space-x-2 hover:bg-stone-50 transition"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>Message Artist</span>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aura-client-empty-state bg-white rounded-[2rem] border border-stone-200 p-12 text-center">
                  <Camera className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No active projects</h3>
                  <p className="text-stone-500 mb-6">Start by finding and booking a photographer</p>
                  <Link 
                    to="/" 
                    className="inline-block bg-stone-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-stone-700 transition"
                  >
                    Browse Photographers
                  </Link>
                </div>
              )}

              {/* Past Projects / History */}
              {completedJobs.length > 0 && (
                <>
                  <h3 className="text-xl font-bold serif pt-4">History</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {completedJobs.map((job) => (
                      <Link 
                        key={job.id}
                        to={`/gallery/${job.id}`}
                        className="bg-white p-6 rounded-[1.5rem] border border-stone-100 flex items-center justify-between group hover:border-stone-300 transition"
                        data-testid={`history-job-${job.id}`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-xl bg-stone-100 overflow-hidden">
                            {job.delivered_images && job.delivered_images[0] ? (
                              <img src={getOptimizedImageUrl(job.delivered_images[0], 'thumb')} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Camera className="w-5 h-5 text-stone-300" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-bold">{job.title}</div>
                            <div className="text-[10px] text-stone-400 italic">
                              {job.photographer_name} • Completed
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-900 transition" />
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {/* Recent Conversations */}
              {conversations.length > 0 && (
                <>
                  <h3 className="text-xl font-bold serif pt-4">Recent Messages</h3>
                  <div className="aura-client-message-list bg-white rounded-[1.5rem] border border-stone-200 overflow-hidden">
                    <div className="divide-y divide-stone-50">
                      {conversations.slice(0, 3).map((conv) => (
                        <div 
                          key={conv.id}
                          onClick={() => openChat(conv)}
                          className="p-4 flex items-center space-x-4 hover:bg-stone-50 cursor-pointer transition"
                          data-testid={`conversation-${conv.id}`}
                        >
                          {conv.other_user_img ? (
                            <img src={conv.other_user_img} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center">
                              <User className="w-5 h-5 text-stone-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{conv.other_user_name || conv.photographer_name}</p>
                            <p className="text-xs text-stone-500 truncate">{conv.last_message || 'No messages yet'}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-stone-300" />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right Column: Account & Sidebar */}
            <div className="space-y-6">
              {/* Payment Status */}
              <div className="aura-client-payment-card bg-stone-900 text-white p-8 rounded-[2rem] shadow-xl relative overflow-hidden" data-testid="payment-card">
                <div className="relative z-10">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-6">Payment Overview</h3>
                  <div className="mb-6">
                    <div className="text-3xl font-bold serif mb-1">${calculateBalance().toLocaleString()}</div>
                    <div className="text-[10px] text-stone-400 font-bold uppercase">Balance Remaining</div>
                  </div>
                  <button
                    type="button"
                    onClick={handlePayBalance}
                    disabled={!actionBooking}
                    className="w-full bg-white text-stone-900 py-3 rounded-xl text-sm font-bold hover:bg-stone-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {signatureBooking ? 'View & Sign Contract' : payableBooking ? 'Pay Invoice' : 'No Balance Due'}
                  </button>
                </div>
                {/* Decorative Circle */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/5 rounded-full"></div>
              </div>

              {/* Artist Recommendation */}
              {recommendedPhotographer && (
                <div className="aura-client-recommendation-card bg-white p-8 rounded-[2rem] border border-stone-200" data-testid="recommendation-card">
                  <h3 className="text-sm font-bold mb-4">You might also like...</h3>
                  <div className="flex items-center space-x-4 mb-6">
                    <img 
                      src={recommendedPhotographer.img} 
                      alt={recommendedPhotographer.name}
                      className="w-12 h-12 rounded-full border border-stone-100 object-cover"
                    />
                    <div>
                      <div className="text-sm font-bold">{recommendedPhotographer.name}</div>
                      <div className="text-[10px] text-stone-400 italic serif">
                        {recommendedPhotographer.category} • {recommendedPhotographer.location}
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/"
                    className="block w-full py-2 bg-stone-50 text-stone-900 rounded-lg text-xs font-bold text-center hover:bg-stone-100 transition"
                  >
                    View Portfolio
                  </Link>
                </div>
              )}

              {/* Quick Stats */}
              <div className="aura-client-side-card bg-white p-6 rounded-[2rem] border border-stone-200">
                <h3 className="text-sm font-bold mb-4">Your Activity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-500">Total Projects</span>
                    <span className="font-bold">{jobs.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-500">Completed</span>
                    <span className="font-bold text-green-600">{completedJobs.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-500">Active</span>
                    <span className="font-bold text-blue-600">{jobs.filter(j => j.status !== 'completed').length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-500">Images Received</span>
                    <span className="font-bold">{jobs.reduce((acc, j) => acc + (j.delivered_images?.length || 0), 0)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Help */}
              <div className="bg-white p-6 rounded-[2rem] border border-stone-200">
                <h3 className="text-sm font-bold mb-3 flex items-center">
                  <HelpCircle className="w-4 h-4 mr-2 text-stone-400" /> Need help?
                </h3>
                <p className="text-xs text-stone-500 leading-relaxed">
                  Having trouble with your gallery or payment? Our support team is here 24/7.
                </p>
                <button
                  type="button"
                  onClick={() => { window.location.href = 'mailto:support@aura.app?subject=Aura%20support%20request'; }}
                  className="mt-4 text-xs font-bold underline hover:no-underline"
                >
                  Contact Support
                </button>
              </div>
            </div>
            </div>
          </>
        )}
      </main>

      {/* Simple Footer */}
      <footer className="py-10 text-center border-t border-stone-100">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-stone-300">
          Aura Management Platform
        </div>
      </footer>

      {/* Gallery Modal */}
      {showGallery && selectedJob && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setShowGallery(false)}>
          <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex justify-between items-center bg-gradient-to-r from-stone-50 to-white">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-stone-900 flex items-center justify-center">
                    <Images className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-xl">Your Private Gallery</h3>
                </div>
                <p className="text-sm text-stone-500">{selectedJob.title} • by {selectedJob.photographer_name}</p>
              </div>
              <button 
                onClick={() => setShowGallery(false)} 
                className="p-3 hover:bg-stone-100 rounded-full transition"
                data-testid="close-gallery-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] bg-stone-50">
              {selectedJob.delivered_images && selectedJob.delivered_images.length > 0 ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm text-stone-500">
                      {selectedJob.delivered_images.length} images delivered
                    </p>
                    <p className="text-xs text-stone-400">Click image to view full size</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedJob.delivered_images.map((img, idx) => (
                      <a 
                        key={idx}
                        href={getOptimizedImageUrl(img, 'full')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group relative"
                        data-testid={`gallery-image-${idx}`}
                      >
                        <div className="aspect-square bg-stone-200 rounded-xl overflow-hidden shadow-md">
                          <img 
                            src={getOptimizedImageUrl(img, 'thumb')} 
                            alt={`Image ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-xl transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-3 py-1.5 rounded-full text-xs font-medium">
                            View Full Size
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center mx-auto mb-4">
                    <Images className="w-10 h-10 text-stone-300" />
                  </div>
                  <h4 className="text-lg font-bold mb-2">No Images Yet</h4>
                  <p className="text-stone-500">Your photographer hasn't delivered images yet.<br/>Check back soon!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal 
          photographers={photographers}
          selectedPhotographer={selectedPhotographer}
          activeBooking={activeBooking}
          token={token}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedPhotographer(null);
            setActiveBooking(null);
          }}
          onBookingCreated={(booking) => {
            setActiveBooking(booking);
            loadData();
          }}
          onBookingComplete={() => {
            setShowBookingModal(false);
            setActiveBooking(null);
            loadData();
          }}
        />
      )}

      {/* Chat Modal */}
      {selectedConversation && activeSection !== 'messages' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeChat}>
          <div 
            className="bg-white rounded-3xl w-full max-w-lg h-[600px] flex flex-col overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Chat Header */}
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {selectedConversation.other_user_img ? (
                  <img src={selectedConversation.other_user_img} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center">
                    <User className="w-5 h-5 text-stone-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold">{selectedConversation.other_user_name || selectedConversation.photographer_name}</h3>
                  <p className="text-xs text-stone-400">Photographer</p>
                </div>
              </div>
              <button 
                onClick={closeChat}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition"
                data-testid="close-chat-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-stone-400 py-12">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-stone-200" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                        msg.sender_type === 'client' 
                          ? 'bg-stone-900 text-white rounded-br-md' 
                          : 'bg-stone-100 text-stone-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {renderMessageContent(msg.content, msg.sender_type === 'client' ? 'underline underline-offset-2 text-white' : 'underline underline-offset-2 text-stone-900')}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-stone-100">
              <div className="flex items-center space-x-3">
                <input
	                  type="text"
	                  value={newMessage}
	                  onChange={(e) => setNewMessage(e.target.value)}
	                  onKeyDown={(e) => {
	                    if (e.key === 'Enter') {
	                      e.preventDefault();
	                      sendMessage();
	                    }
	                  }}
	                  placeholder="Type a message..."
                  className="flex-1 px-4 py-3 bg-stone-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="w-12 h-12 bg-stone-900 text-white rounded-xl flex items-center justify-center hover:bg-stone-700 disabled:opacity-50 transition"
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== PHOTOGRAPHER PRO DASHBOARD ====================
const PhotographerDashboard = () => {
  const { user, userType, token, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { section: routeSection } = useParams();
  const [activeSection, setActiveSectionState] = useState(() => getProSectionFromRoute(routeSection));
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [myAvailability, setMyAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [boostPricing, setBoostPricing] = useState({
    single: 9.99,
    pack3: 26.99,
    pack5: 39.99
  });
  const [availabilityMonth, setAvailabilityMonth] = useState(new Date().getMonth() + 1);
  const [availabilityYear, setAvailabilityYear] = useState(new Date().getFullYear());
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [deliveryModal, setDeliveryModal] = useState(null); // Job to deliver images to
  const [deliveryImages, setDeliveryImages] = useState([]);
  const [newDeliveryUrl, setNewDeliveryUrl] = useState('');
  const [deliveryUploading, setDeliveryUploading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState([]);
  const deliveryInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const portfolioInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const setActiveSection = useCallback((nextSection) => {
    const normalizedSection = getProSectionFromRoute(nextSection);
    setActiveSectionState(normalizedSection);
    const nextPath = normalizedSection === 'dashboard' ? '/pro' : `/pro/${normalizedSection}`;
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  }, [location.pathname, navigate]);

  // State for Create Gallery modal
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [galleryForm, setGalleryForm] = useState({
    title: '',
    description: '',
    client_email: '',
    client_name: ''
  });
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [gallerySubmitting, setGallerySubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  
  // State for Galleries Management
  const [myGalleries, setMyGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [editingGallery, setEditingGallery] = useState(null);
  const [editGalleryImages, setEditGalleryImages] = useState([]);
  const [editGalleryUploading, setEditGalleryUploading] = useState(false);
  const [deletingGalleryId, setDeletingGalleryId] = useState(null);
  const [shareGallery, setShareGallery] = useState(null);
  const [sharingGalleryId, setSharingGalleryId] = useState(null);
  const [copyingGalleryId, setCopyingGalleryId] = useState(null);
  const [galleryNotice, setGalleryNotice] = useState(null);
  const editGalleryInputRef = useRef(null);

  const showGalleryNotice = (message, type = 'success') => {
    setGalleryNotice({ message, type });
    window.setTimeout(() => setGalleryNotice(null), 3500);
  };

  const formatBoostPrice = (price) => `$${Number(price || 0).toFixed(2)}`;

  // Generate notifications from current data
  const getNotifications = () => {
    const notifications = [];
    
    // New messages from clients
    conversations.forEach(conv => {
      if (conv.last_message) {
        notifications.push({
          id: `msg-${conv.id}-${conv.last_message_at || conv.last_message}`,
          type: 'message',
          title: 'New Inquiry',
          description: `${conv.client_name}: ${conv.last_message.slice(0, 40)}...`,
          time: 'Recently',
          icon: 'message',
          action: () => { openChat(conv); setShowNotifications(false); }
        });
      }
    });

    // New booking requests
    bookings.filter(b => b.status === 'pending').forEach(booking => {
      notifications.push({
        id: `booking-${booking.id}`,
        type: 'booking',
        title: 'New Booking Request',
        description: `Session requested for ${booking.booking_date || 'a future date'}`,
        time: 'Recently',
        icon: 'calendar',
        action: () => { setActiveSection('bookings'); setShowNotifications(false); }
      });
    });

    // Jobs needing attention (in progress)
    jobs.filter(j => j.status === 'in_progress').forEach(job => {
      notifications.push({
        id: `job-${job.id}`,
        type: 'job',
        title: 'Job In Progress',
        description: `${job.title || 'Project'} - ready for delivery`,
        time: 'Recently',
        icon: 'camera',
        action: () => { setActiveSection('bookings'); setShowNotifications(false); }
      });
    });

    return notifications
      .filter((notification) => !dismissedNotificationIds.includes(notification.id))
      .slice(0, 10);
  };

  const markNotificationsRead = () => {
    const visibleNotificationIds = getNotifications().map((notification) => notification.id);
    setDismissedNotificationIds((currentIds) => [...new Set([...currentIds, ...visibleNotificationIds])]);
    setShowNotifications(false);
  };

  const loadChatMessages = async (conversationId) => {
    try {
      const msgs = await api.getMessages(conversationId, token);
      setChatMessages(msgs);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const openChat = (conv) => {
    setSelectedConversation(conv);
    loadChatMessages(conv.id);
  };

  const closeChat = () => {
    setSelectedConversation(null);
    setChatMessages([]);
    setNewMessage('');
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !token) return;
    const outgoingMessage = newMessage.trim();

    setSendingMessage(true);
    try {
      const message = await api.sendMessage({
        photographer_id: selectedConversation.photographer_id,
        conversation_id: selectedConversation.id,
        content: outgoingMessage
      }, token);
      setChatMessages((currentMessages) => [...currentMessages, message]);
      setNewMessage('');
      // Refresh conversations to update last_message
      const convs = await api.getConversations(token);
      setConversations(convs);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [statsData, convs, jobsList, bookingsList, configData] = await Promise.all([
        api.getPhotographerStats(token),
        api.getConversations(token),
        api.getJobs(token),
        api.getBookings(token),
        api.getSiteConfig()
      ]);
      setStats(statsData);
      setConversations(convs);
      setJobs(jobsList);
      setBookings(bookingsList);
      setBoostPricing({
        single: configData.boost_price_single ?? 9.99,
        pack3: configData.boost_price_pack3 ?? 26.99,
        pack5: configData.boost_price_pack5 ?? 39.99
      });
      
      // Load portfolio images from user data
      const photographerData = await axios.get(`${API}/photographers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolioImages(photographerData.data.portfolio_images || []);
      setProfileImage(photographerData.data.img || '');
      
      // Load availability
      const availData = await api.getMyAvailability(token);
      setMyAvailability(availData.available_dates || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (userType !== 'photographer') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [isAuthenticated, userType, navigate, loadData]);

  useEffect(() => {
    const normalizedSection = getProSectionFromRoute(routeSection);
    setActiveSectionState(normalizedSection);
  }, [routeSection]);

  const updateJobStatus = async (jobId, status, images = null) => {
    try {
      const updateData = { status };
      if (images) {
        updateData.delivered_images = images;
      }
      await api.updateJob(jobId, updateData, token);
      loadData();
    } catch (error) {
      console.error('Failed to update job:', error);
    }
  };

  // Open delivery modal for a job
  const openDeliveryModal = (job) => {
    setDeliveryModal(job);
    setDeliveryImages(job.delivered_images || []);
    setNewDeliveryUrl('');
  };

  // Close delivery modal
  const closeDeliveryModal = () => {
    setDeliveryModal(null);
    setDeliveryImages([]);
    setNewDeliveryUrl('');
  };

  // Add image URL to delivery list
  const addDeliveryImageUrl = () => {
    if (!newDeliveryUrl.trim()) return;
    if (!newDeliveryUrl.match(/^https?:\/\/.+/)) {
      alert('Please enter a valid image URL');
      return;
    }
    setDeliveryImages([...deliveryImages, newDeliveryUrl]);
    setNewDeliveryUrl('');
  };

  // Upload file for delivery
  const handleDeliveryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setDeliveryUploading(true);
    try {
      const result = await api.uploadGalleryImage(file, token);
      setDeliveryImages([...deliveryImages, result.url]);
    } catch (error) {
      console.error('Failed to upload delivery image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setDeliveryUploading(false);
    }
  };

  // Remove image from delivery list
  const removeDeliveryImage = (index) => {
    setDeliveryImages(deliveryImages.filter((_, i) => i !== index));
  };

  // Save delivered images and mark job complete
  const submitDelivery = async () => {
    if (deliveryImages.length === 0) {
      alert('Please add at least one image to deliver');
      return;
    }
    try {
      let updatedGallery;
      if (deliveryModal.booking_id && deliveryModal.status === 'approvedForFinalDelivery') {
        const result = await api.sendFinalGallery(deliveryModal.booking_id, {
          images: deliveryImages,
          title: deliveryModal.title,
          note: deliveryModal.description
        }, token);
        updatedGallery = result.gallery;
        showGalleryNotice('Final gallery delivered and escrow release checked.');
      } else if (deliveryModal.booking_id) {
        updatedGallery = await api.sendPreviewGallery(deliveryModal.booking_id, {
          images: deliveryImages,
          title: deliveryModal.title,
          note: deliveryModal.description
        }, token);
        showGalleryNotice('Preview gallery sent in messages.');
      } else {
        updatedGallery = await api.updateJob(deliveryModal.id, { 
          status: 'completed', 
          delivered_images: deliveryImages 
        }, token);
      }
      closeDeliveryModal();
      loadData();
      loadMyGalleries();
      setShareGallery(updatedGallery);
    } catch (error) {
      console.error('Failed to deliver images:', error);
      alert('Failed to deliver images. Please try again.');
    }
  };

  // Add more images to an existing delivery
  const addMoreImages = async () => {
    try {
      const updatedGallery = await api.updateJob(deliveryModal.id, { 
        delivered_images: deliveryImages 
      }, token);
      closeDeliveryModal();
      loadData();
      loadMyGalleries();
      setShareGallery(updatedGallery);
    } catch (error) {
      console.error('Failed to update delivery:', error);
      alert('Failed to update images. Please try again.');
    }
  };

  // ==================== CREATE GALLERY FUNCTIONS ====================
  
  // Load clients for gallery creation
  const loadClients = async () => {
    try {
      // Get unique clients from conversations
      const uniqueClients = [];
      const seenIds = new Set();
      conversations.forEach(conv => {
        if (conv.client_id && !seenIds.has(conv.client_id)) {
          seenIds.add(conv.client_id);
          uniqueClients.push({
            id: conv.client_id,
            name: conv.client_name,
            email: conv.client_email || ''
          });
        }
      });
      setClients(uniqueClients);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  // Open gallery creation modal
  const openCreateGallery = () => {
    loadClients();
    setShowCreateGallery(true);
    setGalleryForm({ title: '', description: '', client_email: '', client_name: '' });
    setGalleryImages([]);
    setShareGallery(null);
  };

  const closeCreateGallery = () => {
    setShowCreateGallery(false);
    setGalleryForm({ title: '', description: '', client_email: '', client_name: '' });
    setGalleryImages([]);
  };

  // Handle gallery image upload
  const handleGalleryImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setGalleryUploading(true);
    try {
      for (const file of files) {
        const result = await api.uploadGalleryImage(file, token);
        setGalleryImages(prev => [...prev, result.url]);
      }
    } catch (error) {
      console.error('Failed to upload gallery image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setGalleryUploading(false);
    }
  };

  // Remove image from gallery
  const removeGalleryImage = (index) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
  };

  // Submit gallery creation
  const submitGallery = async () => {
    if (!galleryForm.title.trim()) {
      alert('Please enter a gallery title');
      return;
    }
    if (!galleryForm.client_email.trim()) {
      alert('Please enter client email');
      return;
    }
    if (galleryImages.length === 0) {
      alert('Please add at least one image');
      return;
    }

    setGallerySubmitting(true);
    try {
      const response = await axios.post(`${API}/galleries`, {
        title: galleryForm.title,
        description: galleryForm.description,
        client_email: galleryForm.client_email,
        client_name: galleryForm.client_name,
        images: galleryImages,
        allow_downloads: true,
        allow_favorites: true,
        allow_sharing: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      closeCreateGallery();
      loadData();
      loadMyGalleries();
      setShareGallery(response.data.gallery);
    } catch (error) {
      console.error('Failed to create gallery:', error);
      alert(error.response?.data?.detail || 'Failed to create gallery. Please try again.');
    } finally {
      setGallerySubmitting(false);
    }
  };

  // ==================== GALLERY MANAGEMENT FUNCTIONS ====================
  
  // Load all galleries created by photographer
  const loadMyGalleries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/photographer/galleries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyGalleries(response.data);
    } catch (error) {
      console.error('Failed to load galleries:', error);
    }
  }, [token]);

  // Open gallery for editing
  const openEditGallery = (gallery) => {
    setEditingGallery(gallery);
    setEditGalleryImages([...gallery.delivered_images]);
  };

  // Close edit gallery modal
  const closeEditGallery = () => {
    setEditingGallery(null);
    setEditGalleryImages([]);
  };

  // Upload image to editing gallery
  const handleEditGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setEditGalleryUploading(true);
    try {
      for (const file of files) {
        const result = await api.uploadGalleryImage(file, token);
        setEditGalleryImages(prev => [...prev, result.url]);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setEditGalleryUploading(false);
    }
  };

  // Remove image from editing gallery
  const removeEditGalleryImage = (index) => {
    setEditGalleryImages(editGalleryImages.filter((_, i) => i !== index));
  };

  // Save gallery changes
  const saveGalleryChanges = async () => {
    if (editGalleryImages.length === 0) {
      alert('Gallery must have at least one image');
      return;
    }
    
    try {
      await axios.put(`${API}/galleries/${editingGallery.id}`, {
        delivered_images: editGalleryImages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      closeEditGallery();
      loadMyGalleries();
      alert('Gallery updated successfully!');
    } catch (error) {
      console.error('Failed to update gallery:', error);
      alert('Failed to update gallery. Please try again.');
    }
  };

  // Delete gallery
  const deleteGallery = async (galleryId) => {
    if (!confirm('Are you sure you want to delete this gallery? This action cannot be undone.')) {
      return;
    }
    
    setDeletingGalleryId(galleryId);
    try {
      await axios.delete(`${API}/galleries/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMyGalleries();
    } catch (error) {
      console.error('Failed to delete gallery:', error);
      alert('Failed to delete gallery. Please try again.');
    } finally {
      setDeletingGalleryId(null);
    }
  };

  const copyGalleryLink = async (gallery) => {
    if (!gallery?.id) return;
    setCopyingGalleryId(gallery.id);
    try {
      await copyTextToClipboard(getGalleryShareUrl(gallery.id));
      showGalleryNotice('Private gallery link copied.');
    } catch (error) {
      console.error('Failed to copy gallery link:', error);
      showGalleryNotice('Could not copy link. Open the gallery and copy it from the address bar.', 'error');
    } finally {
      setCopyingGalleryId(null);
    }
  };

  const sendGalleryLink = async (gallery) => {
    if (!gallery?.id || !token) return;
    setSharingGalleryId(gallery.id);
    try {
      const result = await api.shareGalleryLink(gallery.id, {
        gallery_url: getGalleryShareUrl(gallery.id)
      }, token);
      const convs = await api.getConversations(token);
      setConversations(convs);
      if (selectedConversation?.id === result.conversation?.id) {
        loadChatMessages(result.conversation.id);
      }
      showGalleryNotice(`Private gallery link sent to ${gallery.client_name || gallery.client_email}.`);
    } catch (error) {
      console.error('Failed to send gallery link:', error);
      showGalleryNotice(error.response?.data?.detail || 'Failed to send gallery link.', 'error');
    } finally {
      setSharingGalleryId(null);
    }
  };

  // Load galleries when section changes to galleries
  useEffect(() => {
    if (activeSection === 'galleries' && token) {
      loadMyGalleries();
    }
  }, [activeSection, token, loadMyGalleries]);

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await api.uploadProfilePhoto(file, token);
      setProfileImage(result.url);
      // Update user context
      loadData();
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await api.uploadPortfolioImage(file, token);
      setPortfolioImages((currentImages) => [...currentImages, result.url]);
    } catch (error) {
      console.error('Failed to upload portfolio image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const addPortfolioImage = async () => {
    const trimmedImageUrl = newImageUrl.trim();
    if (!trimmedImageUrl) return;
    if (!/^https?:\/\/\S+\.\S+/.test(trimmedImageUrl)) {
      showGalleryNotice('Please enter a valid image URL.', 'error');
      return;
    }
    try {
      const updatedImages = [...portfolioImages, trimmedImageUrl];
      await api.updatePhotographerProfile({ portfolio_images: updatedImages }, token);
      setPortfolioImages(updatedImages);
      setNewImageUrl('');
      showGalleryNotice('Portfolio image added.');
    } catch (error) {
      console.error('Failed to add image:', error);
      showGalleryNotice('Failed to add portfolio image.', 'error');
    }
  };

  const removePortfolioImage = async (index) => {
    try {
      const updatedImages = portfolioImages.filter((_, i) => i !== index);
      await api.updatePhotographerProfile({ portfolio_images: updatedImages }, token);
      setPortfolioImages(updatedImages);
    } catch (error) {
      console.error('Failed to remove image:', error);
    }
  };

  const loadSubscription = useCallback(async () => {
    if (!token) return;
    setSubscriptionLoading(true);
    try {
      const subData = await api.getSubscription(token);
      setSubscription(subData);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [token]);

  const handleChangePlan = async (plan) => {
    if (!token) return;
    setSubscriptionLoading(true);
    try {
      await api.changePlan(plan, token);
      await loadSubscription();
    } catch (error) {
      console.error('Failed to change plan:', error);
      alert('Failed to change plan. Please try again.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleActivateBoost = async () => {
    if (!token) return;
    setSubscriptionLoading(true);
    try {
      await api.activateBoost(token);
      await loadSubscription();
    } catch (error) {
      console.error('Failed to activate boost:', error);
      alert(error.response?.data?.detail || 'Failed to activate boost. Please try again.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handlePurchaseBoosts = async (packageType) => {
    if (!token) return;
    setSubscriptionLoading(true);
    try {
      await api.purchaseBoosts(packageType, token);
      await loadSubscription();
    } catch (error) {
      console.error('Failed to purchase boosts:', error);
      alert('Failed to purchase boosts. Please try again.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Load subscription when membership section is active
  useEffect(() => {
    if (activeSection === 'membership' && !subscription) {
      loadSubscription();
    }
  }, [activeSection, subscription, loadSubscription]);

  // Availability management functions
  const toggleDateAvailability = async (dateStr) => {
    let newAvailability;
    if (myAvailability.includes(dateStr)) {
      newAvailability = myAvailability.filter(d => d !== dateStr);
    } else {
      newAvailability = [...myAvailability, dateStr].sort();
    }
    setMyAvailability(newAvailability);
    
    try {
      await api.updateMyAvailability(newAvailability, token);
    } catch (error) {
      console.error('Failed to update availability:', error);
      // Revert on error
      setMyAvailability(myAvailability);
    }
  };

  const handleApproveBooking = async (bookingId) => {
    const booking = bookings.find((item) => item.id === bookingId);
    const defaultAmount = booking?.total_amount || booking?.deposit_amount || 0;
    const amountInput = window.prompt('Enter photographer invoice amount before Aura fee', defaultAmount ? String(defaultAmount) : '');
    if (amountInput === null) return;
    const photographerAmount = Number(amountInput);
    if (!Number.isFinite(photographerAmount) || photographerAmount <= 0) {
      alert('Invoice amount must be greater than 0.');
      return;
    }
    const auraFee = photographerAmount * 0.10;
    const totalDue = photographerAmount + auraFee;
    if (!window.confirm(`Send invoice?\nPhotographer fee: $${photographerAmount.toFixed(2)}\nAura fee (10%): $${auraFee.toFixed(2)}\nTotal due: $${totalDue.toFixed(2)}`)) {
      return;
    }
    try {
      setBookings((current) => current.map((item) => (
        item.id === bookingId
          ? { ...item, status: 'pendingSignature', request_status: 'invoiced', photographer_amount: photographerAmount, aura_fee: auraFee, total_due: totalDue }
          : item
      )));
      await api.acceptBookingRequest(bookingId, photographerAmount, token);
      loadData();
    } catch (error) {
      console.error('Failed to approve booking:', error);
      alert(error.response?.data?.detail || 'Failed to send invoice');
      loadData();
    }
  };

  const handleRejectBooking = async (bookingId) => {
    const reason = window.prompt('Optional decline reason', '') || '';
    try {
      setBookings((current) => current.map((booking) => (
        booking.id === bookingId ? { ...booking, status: 'declined', request_status: 'declined' } : booking
      )));
      await api.declineBookingRequest(bookingId, reason, token);
      loadData();
    } catch (error) {
      console.error('Failed to reject booking:', error);
      alert(error.response?.data?.detail || 'Failed to reject booking');
      loadData();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-stone-100 text-stone-700';
      case 'pendingSignature': return 'bg-blue-100 text-blue-700';
      case 'signed': return 'bg-blue-100 text-blue-700';
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-purple-100 text-purple-700';
      case 'approvedForFinalDelivery': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (!isAuthenticated || userType !== 'photographer') return null;

  const sidebarLinks = [
    { id: 'dashboard', icon: BarChart3, label: 'Studio Overview', faIcon: 'fa-compass' },
    { id: 'galleries', icon: Images, label: 'Client Archives', faIcon: 'fa-film' },
    { id: 'bookings', icon: Calendar, label: 'Schedule', badge: stats?.pending_jobs, faIcon: 'fa-calendar-check' },
    { id: 'messages', icon: MessageSquare, label: 'Messages', badge: stats?.unread_messages, faIcon: 'fa-envelope' },
    { id: 'portfolio', icon: Camera, label: 'Portfolio', faIcon: 'fa-camera' },
    { id: 'availability', icon: CalendarPlus, label: 'Availability', faIcon: 'fa-calendar-plus' },
    { id: 'profile', icon: User, label: 'Profile', faIcon: 'fa-user' },
    { id: 'earnings', icon: Wallet, label: 'Earnings', faIcon: 'fa-wallet' },
    { id: 'membership', icon: Crown, label: 'Membership', faIcon: 'fa-crown' },
  ];

  const getImageUrl = (img) => {
    return getOptimizedImageUrl(img, 'thumb');
  };

  const studioName = user?.name || 'Focus Point Images';
  const studioFirstName = studioName.split(' ')[0] || studioName;
  const profileCompletion = Math.max(0, Math.min(100, stats?.profile_completion ?? 85));
  const totalRevenue = Number(stats?.total_revenue || 0);
  const profileViews = Number(stats?.profile_views || 0);
  const formattedProfileViews = profileViews >= 1000
    ? `${(profileViews / 1000).toFixed(1)}k`
    : profileViews.toLocaleString();
  const recentInquiry = conversations[0];
  const upcomingJob = jobs.find((job) => ['confirmed', 'in_progress', 'approvedForFinalDelivery'].includes(job.status)) || jobs[0];
  const upcomingBooking = bookings.find((booking) => ['approved', 'pendingSignature', 'signed', 'confirmed', 'paid'].includes(booking.status)) || bookings[0];
  const upcomingShootTitle = upcomingJob?.title || upcomingBooking?.session_type || 'Portrait Session';
  const upcomingShootDate = upcomingJob?.scheduled_date || upcomingBooking?.date || upcomingBooking?.booking_date;
  const upcomingShootTime = upcomingBooking?.time || upcomingJob?.time || '10:00 AM';
  const monthlyBookingCount = Math.max(stats?.total_jobs || 0, jobs.length + bookings.length);
  const portfolioFallbackImages = [
    'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&q=80&w=400',
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=400',
    'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&q=80&w=400',
    'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&q=80&w=400'
  ];
  const dashboardPortfolioImages = (portfolioImages.length > 0 ? portfolioImages : portfolioFallbackImages).slice(0, 4);
  const remainingPortfolioCount = Math.max(portfolioImages.length - 4, 0);
  const promoImage = 'https://images.unsplash.com/photo-1567126542353-3188bc1cf6cb?auto=format&fit=crop&q=80&w=1200';
  const upcomingShootImage = getImageUrl(profileImage) || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300';
  const proRedesignContext = {
    activeSection,
    stats,
    conversations,
    jobs,
    bookings,
    myAvailability,
    availabilityMonth,
    setAvailabilityMonth,
    availabilityYear,
    setAvailabilityYear,
    myGalleries,
    portfolioImages,
    newImageUrl,
    setNewImageUrl,
    uploading,
    profileImage,
    profileInputRef,
    portfolioInputRef,
    handleProfilePhotoUpload,
    handlePortfolioUpload,
    addPortfolioImage,
    removePortfolioImage,
    user,
    subscription,
    subscriptionLoading,
    boostPricing,
    formatBoostPrice,
    handleChangePlan,
    handleActivateBoost,
    handlePurchaseBoosts,
    openCreateGallery,
    copyGalleryLink,
    sendGalleryLink,
    openEditGallery,
    deleteGallery,
    copyingGalleryId,
    sharingGalleryId,
    deletingGalleryId,
    openChat,
    selectedConversation,
    chatMessages,
    newMessage,
    setNewMessage,
    sendChatMessage,
    sendingMessage,
    updateJobStatus,
    openDeliveryModal,
    toggleDateAvailability,
    handleApproveBooking,
    handleRejectBooking,
    getInitials,
    getImageUrl,
    setActiveSection,
    showGalleryNotice,
    studioName,
    profileCompletion,
    totalRevenue,
    formattedProfileViews
  };

  return (
    <div className="aura-pro-shell aura-pro-suite flex min-h-screen bg-[#f5f5f4] text-stone-900">
      {galleryNotice && (
        <div className={`fixed top-6 right-6 z-[90] px-5 py-4 rounded-xl shadow-2xl border flex items-center gap-3 max-w-sm ${
          galleryNotice.type === 'error'
            ? 'bg-red-50 border-red-100 text-red-700'
            : 'bg-white border-stone-200 text-stone-900'
        }`}>
          <div className={`w-2 h-2 rounded-full ${galleryNotice.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}></div>
          <p className="text-sm font-medium">{galleryNotice.message}</p>
        </div>
      )}

      {/* Sidebar Navigation */}
      <aside className="aura-pro-sidebar w-64 bg-white border-r border-stone-200 hidden lg:flex flex-col fixed h-full" data-testid="pro-sidebar">
        <div className="p-8">
          <Link to="/" className="aura-logo text-2xl font-bold serif italic mb-6 block">AURA</Link>
          
          {/* Create Gallery Button */}
          <button
            onClick={openCreateGallery}
            className="aura-cta w-full mb-6 px-4 py-3 text-sm font-bold flex items-center justify-center space-x-2 transition"
            data-testid="create-gallery-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Create Gallery</span>
          </button>
          
          <nav className="space-y-1">
            {sidebarLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => setActiveSection(link.id)}
                className={`aura-pro-nav-item w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition ${
                  activeSection === link.id 
                    ? 'is-active bg-stone-900 text-white' 
                    : 'text-stone-500 hover:bg-stone-100'
                }`}
                data-testid={`sidebar-${link.id}`}
              >
                <link.icon className="w-5 h-5" />
                <span>{link.label}</span>
                {link.badge > 0 && (
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${
                    activeSection === link.id ? 'bg-white text-stone-900' : 'bg-stone-900 text-white'
                  }`}>
                    {link.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-auto p-8 border-t border-stone-100">
          <div className="aura-pro-account-card flex items-center space-x-3">
            {getImageUrl(profileImage) ? (
              <img src={getImageUrl(profileImage)} alt={user?.name} className="w-10 h-10 rounded-full border border-stone-200 object-cover" />
            ) : (
              <div className="aura-pro-account-mark w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                <span>{getInitials(studioName)}</span>
              </div>
            )}
            <div className="flex-1">
              <div className="text-xs font-bold">{studioName}</div>
              <div className="text-[10px] text-stone-400 font-medium uppercase">
                {user?.is_admin ? 'Admin' : 'Photographer'}
              </div>
            </div>
            <button 
              onClick={() => { logout(); navigate('/'); }}
              className="text-stone-400 hover:text-stone-900"
              data-testid="pro-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          {/* Admin Link */}
          {user?.is_admin && (
            <Link 
              to="/admin"
              className="mt-4 flex items-center space-x-2 text-xs font-medium text-stone-500 hover:text-stone-900 bg-stone-100 px-3 py-2 rounded-lg"
              data-testid="admin-link"
            >
              <Settings className="w-4 h-4" />
              <span>Admin CMS</span>
            </Link>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="aura-pro-workspace flex-1 lg:ml-64 p-8">
        {/* Header */}
        <header className="aura-pro-page-top flex justify-between items-center mb-10">
          <div>
            <h1 className="text-2xl font-bold serif">Welcome back, {studioFirstName}.</h1>
            <p className="text-stone-400 text-sm">Here&apos;s what&apos;s happening with your studio today.</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 text-stone-400 hover:text-stone-900 transition relative rounded-full hover:bg-stone-100"
                data-testid="notification-bell-photographer"
              >
                <Bell className="w-5 h-5" />
                {getNotifications().length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-stone-200 overflow-hidden z-50">
                  <div className="p-4 border-b border-stone-100 flex justify-between items-center">
                    <h3 className="font-bold">Notifications</h3>
                    <span className="text-xs text-stone-400">{getNotifications().length} new</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {getNotifications().length === 0 ? (
                      <div className="p-8 text-center text-stone-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 text-stone-200" />
                        <p className="text-sm">No new notifications</p>
                      </div>
                    ) : (
                      getNotifications().map((notif) => (
                        <div 
                          key={notif.id}
                          onClick={() => {
                            setDismissedNotificationIds((currentIds) => [...new Set([...currentIds, notif.id])]);
                            if (notif.action) notif.action();
                          }}
                          className="p-4 hover:bg-stone-50 cursor-pointer border-b border-stone-50 last:border-0"
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notif.type === 'message' ? 'bg-blue-100 text-blue-600' :
                              notif.type === 'booking' ? 'bg-green-100 text-green-600' :
                              'bg-amber-100 text-amber-600'
                            }`}>
                              {notif.type === 'message' && <MessageSquare className="w-4 h-4" />}
                              {notif.type === 'booking' && <Calendar className="w-4 h-4" />}
                              {notif.type === 'job' && <Camera className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{notif.title}</p>
                              <p className="text-xs text-stone-500 truncate">{notif.description}</p>
                              <p className="text-xs text-stone-400 mt-1">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {getNotifications().length > 0 && (
                    <div className="p-3 border-t border-stone-100 text-center">
                      <button
                        type="button"
                        onClick={markNotificationsRead}
                        className="text-xs text-stone-500 hover:text-stone-900 font-medium"
                      >
                        Mark all as read
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Link 
              to={`/photographer/${user?.id}`}
              className="bg-stone-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center space-x-2 shadow-lg hover:bg-stone-700 transition"
            >
              <User className="w-4 h-4" />
              <span>View Profile</span>
            </Link>
          </div>
        </header>

        <div className="aura-pro-mobile-nav lg:hidden" aria-label="Pro dashboard navigation">
          <button type="button" onClick={openCreateGallery} className="aura-mobile-create">
            <Plus className="w-4 h-4" />
            Gallery
          </button>
          <div>
            {sidebarLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => setActiveSection(link.id)}
                className={activeSection === link.id ? 'is-active' : ''}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
                {link.badge > 0 && <span>{link.badge}</span>}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
          </div>
        ) : (
          <>
            {/* Dashboard View */}
            {activeSection === 'dashboard' && (
              <div className="aura-pro-dashboard" data-testid="pro-dashboard-section">
                <section className="aura-dashboard-stats">
                  <article className="aura-dashboard-stat aura-dashboard-stat-revenue stat-card" data-testid="stat-revenue">
                    <div>
                      <p className="aura-dashboard-label">Total Revenue</p>
                      <strong className="aura-dashboard-number serif">${totalRevenue.toLocaleString()}</strong>
                      <span className="aura-dashboard-trend">
                        <ArrowUp className="w-3.5 h-3.5" />
                        {stats?.revenue_change || 0}% from last month
                      </span>
                    </div>
                    <svg className="aura-dashboard-line" viewBox="0 0 210 82" aria-hidden="true">
                      <path d="M4 70 C 34 44, 46 36, 66 24 S 96 64, 120 48 S 146 4, 174 24 S 196 25, 206 12" />
                      <circle cx="206" cy="12" r="5" />
                    </svg>
                  </article>

                  <article className="aura-dashboard-stat stat-card" data-testid="stat-inquiries">
                    <div>
                      <p className="aura-dashboard-label">Active Inquiries</p>
                      <strong className="aura-dashboard-number serif">{stats?.active_inquiries || 0}</strong>
                      <span className="aura-dashboard-muted">{stats?.pending_response || 0} pending response</span>
                    </div>
                    <span className="aura-dashboard-icon">
                      <MessageSquare className="w-7 h-7" />
                    </span>
                  </article>

                  <article className="aura-dashboard-stat stat-card" data-testid="stat-views">
                    <div>
                      <p className="aura-dashboard-label">Profile Views</p>
                      <strong className="aura-dashboard-number serif">{formattedProfileViews}</strong>
                      <span className="aura-dashboard-muted">+{stats?.views_this_week || 0} this week</span>
                    </div>
                    <span className="aura-dashboard-icon">
                      <Eye className="w-8 h-8" />
                    </span>
                  </article>

                  <article className="aura-dashboard-stat aura-dashboard-stat-health stat-card" data-testid="stat-portfolio-health">
                    <div>
                      <p className="aura-dashboard-label">Profile Health</p>
                      <strong className="aura-dashboard-number serif">{profileCompletion}%</strong>
                      <span className="aura-dashboard-muted">{portfolioImages.length} portfolio image{portfolioImages.length === 1 ? '' : 's'}</span>
                    </div>
                    <span className="aura-dashboard-icon">
                      <CheckCircle className="w-8 h-8" />
                    </span>
                  </article>
                </section>

                <section className="aura-dashboard-grid">
                  <div className="aura-dashboard-main">
                    <article className="aura-pro-panel aura-inquiry-panel">
                      <div className="aura-panel-head">
                        <h3 className="serif">Recent Inquiries</h3>
                        <button type="button" onClick={() => setActiveSection('messages')} className="aura-text-action">
                          View Inbox
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>

                      {recentInquiry ? (
                        <button
                          type="button"
                          onClick={() => openChat(recentInquiry)}
                          className="aura-inquiry-row"
                          data-testid={`inquiry-${recentInquiry.id}`}
                        >
                          <span className="aura-inquiry-avatar">{getInitials(recentInquiry.client_name)}</span>
                          <span className="aura-inquiry-copy">
                            <strong>{recentInquiry.client_name}</strong>
                            <em>{recentInquiry.last_message || 'New conversation'}...</em>
                          </span>
                          <span className="aura-status-pill">New</span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      ) : (
                        <button type="button" onClick={() => setActiveSection('messages')} className="aura-inquiry-row is-empty">
                          <span className="aura-inquiry-avatar"><MessageSquare className="w-5 h-5" /></span>
                          <span className="aura-inquiry-copy">
                            <strong>No inquiries yet</strong>
                            <em>New client messages will appear here.</em>
                          </span>
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      )}
                    </article>

                    <article
                      className="aura-portfolio-promo"
                      style={{ '--promo-image': `url(${promoImage})` }}
                    >
                      <div className="aura-portfolio-promo-copy">
                        <p className="aura-dashboard-label">Elevate Your Portfolio</p>
                        <h3 className="serif">Curate. Showcase. Inspire.</h3>
                        <p>A strong portfolio attracts dream clients.</p>
                        <button type="button" onClick={() => setActiveSection('portfolio')} className="aura-dark-action">
                          Optimize Portfolio
                          <ArrowUp className="w-4 h-4 rotate-45" />
                        </button>
                      </div>
                    </article>

                    <div className="aura-dashboard-bottom-grid">
                      <article className="aura-pro-panel aura-mini-card">
                        <div className="aura-panel-head">
                          <h3>Upcoming Shoots</h3>
                          <button type="button" onClick={() => setActiveSection('bookings')} className="aura-text-action">View Schedule</button>
                        </div>
                        <button type="button" onClick={() => setActiveSection('bookings')} className="aura-shoot-row">
                          <img src={upcomingShootImage} alt={upcomingShootTitle} />
                          <span>
                            <strong>{upcomingShootTitle}</strong>
                            <small>
                              {upcomingShootDate
                                ? new Date(upcomingShootDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'May 18, 2024'} • {upcomingShootTime}
                            </small>
                          </span>
                          <em>{upcomingJob?.status || upcomingBooking?.status || 'Confirmed'}</em>
                        </button>
                      </article>

                      <article className="aura-pro-panel aura-mini-card aura-booking-card">
                        <div className="aura-panel-head">
                          <h3>Monthly Bookings</h3>
                          <button type="button" onClick={() => setActiveSection('earnings')} className="aura-text-action">View Report</button>
                        </div>
                        <div className="aura-booking-metric">
                          <span>
                            <strong className="serif">{monthlyBookingCount}</strong>
                            <small>+20% from last month</small>
                          </span>
                          <div className="aura-bars" aria-hidden="true">
                            {[32, 42, 54, 28, 58, 70, 62, 46, 78, 96].map((height, index) => (
                              <i key={index} style={{ height: `${height}%` }} />
                            ))}
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>

                  <aside className="aura-pro-panel aura-portfolio-health">
                    <h3 className="serif">Portfolio Health</h3>
                    <div className="aura-completion-head">
                      <span className="aura-dashboard-label">Profile Completion</span>
                      <strong>{profileCompletion}%</strong>
                    </div>
                    <div className="aura-completion-track">
                      <span style={{ width: `${profileCompletion}%` }} />
                    </div>

                    <div className="aura-health-tip">
                      <span><Sparkles className="w-5 h-5" /></span>
                      <p>Great progress! Add more portfolio images to attract more clients.</p>
                      <button type="button" onClick={() => setActiveSection('portfolio')}>
                        Optimize now
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>

                    <p className="aura-dashboard-label">Portfolio Images</p>
                    <button
                      type="button"
                      className="aura-upload-drop"
                      onClick={() => portfolioInputRef.current?.click()}
                      data-testid="dashboard-portfolio-upload"
                    >
                      <ImagePlus className="w-10 h-10" />
                      <strong>{uploading ? 'Uploading...' : 'Add new images'}</strong>
                      <small>Click to upload JPG, PNG up to 10MB</small>
                    </button>
                    <input
                      ref={portfolioInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePortfolioUpload}
                    />

                    <div className="aura-thumb-row">
                      {dashboardPortfolioImages.map((img, idx) => (
                        <button key={`${img}-${idx}`} type="button" onClick={() => setActiveSection('portfolio')}>
                          <img src={getImageUrl(img)} alt={`Portfolio preview ${idx + 1}`} />
                        </button>
                      ))}
                      <button type="button" onClick={() => setActiveSection('portfolio')} className="aura-more-thumb">
                        <strong>{remainingPortfolioCount > 0 ? `+${remainingPortfolioCount}` : '+'}</strong>
                        <span>{remainingPortfolioCount > 0 ? 'More' : 'Add'}</span>
                      </button>
                    </div>
                  </aside>
                </section>
              </div>
            )}

            {activeSection !== 'dashboard' && (
              <ProRedesignSections {...proRedesignContext} />
            )}

            {/* Bookings View */}
            {activeSection === 'bookings' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Bookings</h2>
                  <div className="flex space-x-2">
                    <span className="px-3 py-1 bg-stone-900 text-white text-xs font-bold rounded-full">
                      {jobs.length} Jobs
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                      {bookings.filter(b => b.status === 'pending').length} Pending Requests
                    </span>
                  </div>
                </div>

                {/* Pending Booking Requests */}
                {bookings.filter(b => b.status === 'pending').length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-600" />
                      Pending Booking Requests
                    </h3>
                    <div className="space-y-3">
                      {bookings.filter(b => b.status === 'pending').map((booking) => (
                        <div key={booking.id} className="bg-white rounded-xl p-4 flex items-center justify-between">
                          <div>
                            <div className="font-bold">{booking.client_name}</div>
                            <div className="text-sm text-stone-500">
                              {new Date(booking.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} • {booking.session_type}
                            </div>
                            {booking.notes && <div className="text-xs text-stone-400 mt-1">{booking.notes}</div>}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApproveBooking(booking.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                              data-testid={`approve-booking-${booking.id}`}
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleRejectBooking(booking.id)}
                              className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-300"
                              data-testid={`reject-booking-${booking.id}`}
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Confirmed Bookings */}
                {bookings.filter(b => ['approved', 'pendingSignature', 'signed', 'confirmed', 'paid'].includes(b.status)).length > 0 && (
                  <div className="bg-white rounded-3xl border border-stone-200 p-6">
                    <h3 className="font-bold mb-4">Confirmed Bookings</h3>
                    <div className="space-y-3">
                      {bookings.filter(b => ['approved', 'pendingSignature', 'signed', 'confirmed', 'paid'].includes(b.status)).map((booking) => (
                        <div key={booking.id} className="p-4 bg-stone-50 rounded-xl flex items-center justify-between">
                          <div>
                            <div className="font-bold">{booking.client_name}</div>
                            <div className="text-sm text-stone-500">
                              {new Date(booking.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                          <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${
                            ['paid', 'confirmed'].includes(booking.status) ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {booking.status === 'confirmed' ? 'Invoice Paid' : booking.status === 'paid' ? 'Deposit Paid' : booking.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Jobs Section */}
                <h3 className="text-lg font-bold pt-4">Jobs / Projects</h3>
                {jobs.length === 0 ? (
                  <div className="bg-white rounded-3xl border border-stone-200 p-12 text-center">
                    <Calendar className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No jobs yet</h3>
                    <p className="text-stone-500">When clients book your services, they&apos;ll appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {jobs.map((job) => (
                      <div key={job.id} className="bg-white rounded-2xl border border-stone-200 p-6" data-testid={`booking-${job.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-stone-100 rounded-2xl flex items-center justify-center font-bold text-stone-400">
                              {getInitials(job.client_name)}
                            </div>
                            <div>
                              <h4 className="font-bold">{job.title}</h4>
                              <p className="text-sm text-stone-500">{job.client_name}</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full ${getStatusColor(job.status)}`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </div>
                        
                        {job.description && (
                          <p className="text-stone-600 text-sm mb-4">{job.description}</p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-stone-500 mb-4">
                          {job.scheduled_date && (
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{job.scheduled_date}</span>
                            </span>
                          )}
                          {job.location && <span>{job.location}</span>}
                        </div>

                        {/* Action Buttons */}
                        {job.status !== 'completed' && job.status !== 'cancelled' && (
                          <div className="flex space-x-2 pt-4 border-t">
                            {job.status === 'pending' && (
                              <button
                                onClick={() => updateJobStatus(job.id, 'confirmed')}
                                className="px-4 py-2 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-700"
                              >
                                Confirm Booking
                              </button>
                            )}
                            {job.status === 'confirmed' && (
                              <button
                                onClick={() => updateJobStatus(job.id, 'in_progress')}
                                className="px-4 py-2 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-700"
                              >
                                Start Job
                              </button>
                            )}
                            {job.status === 'in_progress' && (
                              <button
                                onClick={() => openDeliveryModal(job)}
                                disabled={(job.revision_count || 0) >= 2}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
                                data-testid={`deliver-btn-${job.id}`}
                              >
                                <Image className="w-4 h-4" />
                                {(job.revision_count || 0) >= 2 ? 'Max Revisions Reached' : 'Send Preview'}
                              </button>
                            )}
                            {job.status === 'approvedForFinalDelivery' && (
                              <button
                                onClick={() => openDeliveryModal(job)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
                                data-testid={`deliver-final-btn-${job.id}`}
                              >
                                <Image className="w-4 h-4" />
                                Send Final Gallery
                              </button>
                            )}
                          </div>
                        )}

                        {/* Delivered Images */}
                        {job.delivered_images && job.delivered_images.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-xs font-bold uppercase tracking-widest text-stone-400">
                                Delivered Images ({job.delivered_images.length})
                              </p>
                              {job.status === 'completed' && (
                                <button
                                  onClick={() => openDeliveryModal(job)}
                                  className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
                                  data-testid={`add-more-btn-${job.id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                  Add More
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-6 gap-2">
                              {job.delivered_images.map((img, idx) => (
                                <img key={idx} src={getOptimizedImageUrl(img, 'thumb')} alt={`Delivered ${idx + 1}`} className="aspect-square object-cover rounded-lg" />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Availability Management View */}
            {activeSection === 'availability' && (
              <div className="space-y-6" data-testid="availability-section">
                <div>
                  <h2 className="text-xl font-bold">Manage Availability</h2>
                  <p className="text-stone-500 text-sm mt-1">Click dates to toggle availability for client bookings</p>
                </div>

                <div className="bg-white rounded-3xl border border-stone-200 p-8">
                  {/* Month Navigation */}
                  <div className="flex justify-between items-center mb-6">
                    <button 
                      onClick={() => {
                        if (availabilityMonth === 1) {
                          setAvailabilityMonth(12);
                          setAvailabilityYear(availabilityYear - 1);
                        } else {
                          setAvailabilityMonth(availabilityMonth - 1);
                        }
                      }}
                      className="p-2 hover:bg-stone-100 rounded-lg"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-lg font-bold">
                      {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][availabilityMonth - 1]} {availabilityYear}
                    </span>
                    <button 
                      onClick={() => {
                        if (availabilityMonth === 12) {
                          setAvailabilityMonth(1);
                          setAvailabilityYear(availabilityYear + 1);
                        } else {
                          setAvailabilityMonth(availabilityMonth + 1);
                        }
                      }}
                      className="p-2 hover:bg-stone-100 rounded-lg"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                      <div key={i} className="text-center text-xs font-bold text-stone-400 uppercase py-2">{day}</div>
                    ))}
                    {(() => {
                      const daysInMonth = new Date(availabilityYear, availabilityMonth, 0).getDate();
                      const firstDay = new Date(availabilityYear, availabilityMonth - 1, 1).getDay();
                      const days = [];
                      
                      for (let i = 0; i < firstDay; i++) {
                        days.push(<div key={`empty-${i}`} className="p-4"></div>);
                      }
                      
                      for (let day = 1; day <= daysInMonth; day++) {
                        const dateStr = `${availabilityYear}-${String(availabilityMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const isAvailable = myAvailability.includes(dateStr);
                        const isPast = new Date(dateStr) < new Date(new Date().toDateString());
                        
                        days.push(
                          <button
                            key={day}
                            onClick={() => !isPast && toggleDateAvailability(dateStr)}
                            disabled={isPast}
                            className={`p-4 rounded-xl text-sm font-medium transition ${
                              isPast 
                                ? 'text-stone-300 cursor-not-allowed'
                                : isAvailable 
                                  ? 'bg-green-500 text-white hover:bg-green-600' 
                                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                            }`}
                            data-testid={`avail-day-${day}`}
                          >
                            {day}
                          </button>
                        );
                      }
                      
                      return days;
                    })()}
                  </div>

                  <div className="flex items-center gap-6 mt-6 pt-6 border-t text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-green-500"></div>
                      <span className="text-stone-600">Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-stone-100"></div>
                      <span className="text-stone-600">Unavailable</span>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-2xl p-6">
                  <h3 className="font-bold mb-2">Quick Stats</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold serif">{myAvailability.length}</div>
                      <div className="text-xs text-stone-500">Total Available Days</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold serif">{bookings.filter(b => b.status === 'paid').length}</div>
                      <div className="text-xs text-stone-500">Confirmed Bookings</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold serif">{bookings.filter(b => b.status === 'pending').length}</div>
                      <div className="text-xs text-stone-500">Pending Requests</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Galleries Management View */}
            {activeSection === 'galleries' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">Client Galleries</h2>
                    <p className="text-sm text-stone-500 mt-1">Manage your private client collections</p>
                  </div>
                  <button
                    onClick={openCreateGallery}
                    className="px-5 py-2.5 bg-stone-900 text-white rounded-xl text-sm font-bold flex items-center space-x-2 hover:bg-stone-700 transition"
                  >
                    <Plus className="w-4 h-4" />
                    <span>New Gallery</span>
                  </button>
                </div>
                
                {myGalleries.length === 0 ? (
                  <div className="bg-white rounded-3xl border border-stone-200 p-12 text-center">
                    <Images className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No galleries yet</h3>
                    <p className="text-stone-500 mb-6">Create your first private gallery for a client.</p>
                    <button
                      onClick={openCreateGallery}
                      className="px-6 py-3 bg-stone-900 text-white rounded-xl font-bold text-sm hover:bg-stone-700 transition"
                    >
                      Create Gallery
                    </button>
                  </div>
                ) : (
	                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
	                    {myGalleries.map((gallery) => (
	                      <div 
	                        key={gallery.id}
	                        className="bg-white rounded-2xl border border-stone-200 overflow-hidden group hover:shadow-lg transition"
	                        data-testid={`gallery-card-${gallery.id}`}
	                      >
                        {/* Gallery Preview */}
                        <div className="relative aspect-[16/9] bg-stone-100">
                          {gallery.delivered_images && gallery.delivered_images.length > 0 ? (
                            <div className="grid grid-cols-3 h-full">
                              {gallery.delivered_images.slice(0, 3).map((img, idx) => (
                                <div key={idx} className={`${idx === 0 ? 'col-span-2 row-span-2' : ''} overflow-hidden`}>
	                                  <img 
	                                    src={getImageUrl(img)} 
	                                    alt="" 
	                                    className="w-full h-full object-cover"
	                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Images className="w-12 h-12 text-stone-300" />
                            </div>
                          )}
                          
                          {/* Image count badge */}
                          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-bold">
                            {gallery.delivered_images?.length || 0} images
                          </div>
                        </div>
                        
                        {/* Gallery Info */}
	                        <div className="p-5">
	                          <div className="flex items-start justify-between gap-4">
	                            <div>
	                              <h3 className="font-bold text-lg mb-1">{gallery.title}</h3>
	                              <p className="text-sm text-stone-500">
	                                For: {gallery.client_name || gallery.client_email}
	                              </p>
	                            </div>
	                            <span className="shrink-0 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
	                              Private
	                            </span>
	                          </div>
	                          {gallery.description && (
	                            <p className="text-xs text-stone-400 mt-3 mb-4 line-clamp-2">{gallery.description}</p>
	                          )}

	                          <div className="mt-5 grid grid-cols-3 gap-2">
	                            <Link
	                              to={`/gallery/${gallery.id}`}
	                              className="px-3 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-bold flex items-center justify-center gap-2 transition"
	                              title="Open private gallery"
	                              data-testid={`open-gallery-${gallery.id}`}
	                            >
	                              <ExternalLink className="w-4 h-4" />
	                              <span>Open</span>
	                            </Link>
	                            <button
	                              onClick={() => copyGalleryLink(gallery)}
	                              disabled={copyingGalleryId === gallery.id}
	                              className="px-3 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-bold flex items-center justify-center gap-2 transition disabled:opacity-50"
	                              title="Copy private gallery link"
	                              data-testid={`copy-gallery-${gallery.id}`}
	                            >
	                              <Copy className="w-4 h-4" />
	                              <span>{copyingGalleryId === gallery.id ? 'Copied' : 'Copy'}</span>
	                            </button>
	                            <button
	                              onClick={() => sendGalleryLink(gallery)}
	                              disabled={sharingGalleryId === gallery.id}
	                              className="px-3 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-700 text-white text-xs font-bold flex items-center justify-center gap-2 transition disabled:opacity-50"
	                              title="Send private gallery link in messages"
	                              data-testid={`send-gallery-${gallery.id}`}
	                            >
	                              {sharingGalleryId === gallery.id ? (
	                                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
	                              ) : (
	                                <Send className="w-4 h-4" />
	                              )}
	                              <span>Send</span>
	                            </button>
	                          </div>
	                          
	                          <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                            <span className="text-[10px] text-stone-400 uppercase tracking-widest">
                              {gallery.created_at ? new Date(gallery.created_at).toLocaleDateString() : 'Recently'}
                            </span>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openEditGallery(gallery)}
                                className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition"
                                title="Edit Gallery"
                                data-testid={`edit-gallery-${gallery.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteGallery(gallery.id)}
                                disabled={deletingGalleryId === gallery.id}
                                className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                                title="Delete Gallery"
                                data-testid={`delete-gallery-${gallery.id}`}
                              >
                                {deletingGalleryId === gallery.id ? (
                                  <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Messages View */}
            {activeSection === 'messages' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold">Messages</h2>
                
                {conversations.length === 0 ? (
                  <div className="bg-white rounded-3xl border border-stone-200 p-12 text-center">
                    <MessageSquare className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No messages yet</h3>
                    <p className="text-stone-500">Client inquiries will appear here.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl border border-stone-200 overflow-hidden">
                    <div className="divide-y">
                      {conversations.map((conv) => (
                        <div 
                          key={conv.id}
                          onClick={() => openChat(conv)}
                          className="p-6 flex items-center justify-between hover:bg-stone-50 transition cursor-pointer"
                          data-testid={`message-${conv.id}`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-stone-100 rounded-2xl flex items-center justify-center font-bold text-stone-400">
                              {getInitials(conv.client_name)}
                            </div>
                            <div>
                              <div className="font-bold text-sm">{conv.client_name}</div>
                              <div className="text-xs text-stone-400">{conv.last_message || 'No messages yet'}</div>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-stone-300" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Portfolio View */}
            {activeSection === 'portfolio' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Portfolio</h2>
                  <span className="text-sm text-stone-500">{portfolioImages.length} images</span>
                </div>
                
                <div className="bg-white rounded-3xl border border-stone-200 p-6">
                  {/* Upload Image */}
                  <div className="mb-6">
                    <input
                      type="file"
                      ref={portfolioInputRef}
                      onChange={handlePortfolioUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <div className="flex space-x-3">
                      <button
                        onClick={() => portfolioInputRef.current?.click()}
                        disabled={uploading}
                        className="flex-1 px-6 py-3 bg-stone-900 text-white rounded-xl font-medium hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                        data-testid="upload-portfolio-btn"
                      >
                        <Upload className="w-4 h-4" />
                        <span>{uploading ? 'Uploading...' : 'Upload Image'}</span>
                      </button>
                    </div>
                    <p className="text-xs text-stone-400 mt-2 text-center">Or add via URL:</p>
                  </div>
                  
                  {/* Add via URL */}
                  <div className="flex space-x-3 mb-6">
                    <input
                      type="url"
                      placeholder="Enter image URL..."
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="flex-1 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200"
                      data-testid="portfolio-image-input"
                    />
                    <button
                      onClick={addPortfolioImage}
                      disabled={!newImageUrl.trim()}
                      className="px-6 py-3 border border-stone-200 rounded-xl font-medium hover:bg-stone-50 disabled:opacity-50"
                      data-testid="add-portfolio-image"
                    >
                      Add URL
                    </button>
                  </div>
                  
                  {portfolioImages.length === 0 ? (
                    <div className="text-center py-12">
                      <Images className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                      <h3 className="text-xl font-bold mb-2">No portfolio images</h3>
                      <p className="text-stone-500">Add images to showcase your work to potential clients.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {portfolioImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <div className="aspect-square rounded-xl overflow-hidden bg-stone-100">
                            <img src={getImageUrl(img)} alt={`Portfolio ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                          <button
                            onClick={() => removePortfolioImage(idx)}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                            data-testid={`remove-portfolio-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Profile View */}
            {activeSection === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold">Profile Settings</h2>
                
                <div className="bg-white rounded-3xl border border-stone-200 p-8">
                  {/* Profile Photo Upload */}
                  <div className="flex items-start space-x-8 mb-8 pb-8 border-b">
                    <div className="relative group">
                      <div className="w-32 h-32 rounded-2xl overflow-hidden bg-stone-200">
                        {getImageUrl(profileImage) ? (
                          <img src={getImageUrl(profileImage)} alt={user?.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-12 h-12 text-stone-400" />
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        ref={profileInputRef}
                        onChange={handleProfilePhotoUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <button
                        onClick={() => profileInputRef.current?.click()}
                        disabled={uploading}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center rounded-2xl"
                      >
                        <div className="text-white text-center">
                          <Upload className="w-6 h-6 mx-auto mb-1" />
                          <span className="text-xs font-medium">{uploading ? 'Uploading...' : 'Change Photo'}</span>
                        </div>
                      </button>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2">Profile Photo</h3>
                      <p className="text-stone-500 text-sm mb-4">
                        This photo will be displayed on your public profile in the Discover section where clients can find you.
                      </p>
                      <button
                        onClick={() => profileInputRef.current?.click()}
                        disabled={uploading}
                        className="px-4 py-2 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-700 disabled:opacity-50 flex items-center space-x-2"
                        data-testid="upload-profile-photo-btn"
                      >
                        <Upload className="w-4 h-4" />
                        <span>{uploading ? 'Uploading...' : 'Upload New Photo'}</span>
                      </button>
                      <p className="text-xs text-stone-400 mt-2">Supported: JPG, PNG, WebP. Max 5MB.</p>
                    </div>
                  </div>

                  {/* Profile Info Preview */}
                  <div>
                    <h3 className="font-bold mb-4">Profile Preview</h3>
                    <div className="bg-stone-50 rounded-2xl p-6">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-stone-200">
                          {getImageUrl(profileImage) ? (
                            <img src={getImageUrl(profileImage)} alt={user?.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-8 h-8 text-stone-400" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-bold text-lg">{user?.name}</h4>
                          <p className="text-stone-500 text-sm">{user?.location} • {user?.category}</p>
                        </div>
                      </div>
                      <p className="text-stone-600 text-sm">{user?.bio || 'No bio added yet.'}</p>
                    </div>
                    <Link
                      to={`/photographer/${user?.id}`}
                      className="mt-4 inline-flex items-center space-x-2 text-sm font-medium text-stone-600 hover:text-stone-900"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View Public Profile</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Earnings View */}
            {activeSection === 'earnings' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold">Earnings</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-3xl border border-stone-200">
                    <div className="text-stone-400 text-xs font-bold uppercase tracking-widest mb-2">Total Earnings</div>
                    <div className="text-3xl font-bold serif italic">${stats?.total_revenue?.toLocaleString() || '0'}</div>
                  </div>
                  <div className="bg-white p-6 rounded-3xl border border-stone-200">
                    <div className="text-stone-400 text-xs font-bold uppercase tracking-widest mb-2">Completed Jobs</div>
                    <div className="text-3xl font-bold serif italic">{stats?.completed_jobs || 0}</div>
                  </div>
                  <div className="bg-white p-6 rounded-3xl border border-stone-200">
                    <div className="text-stone-400 text-xs font-bold uppercase tracking-widest mb-2">Avg. Per Job</div>
                    <div className="text-3xl font-bold serif italic">
                      ${stats?.completed_jobs > 0 ? Math.round(stats?.total_revenue / stats?.completed_jobs).toLocaleString() : '0'}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-stone-200 p-6">
                  <h3 className="font-bold mb-4">Recent Transactions</h3>
                  {jobs.filter(j => j.status === 'completed').length === 0 ? (
                    <div className="text-center py-8 text-stone-400">
                      <Wallet className="w-12 h-12 mx-auto mb-3 text-stone-200" />
                      <p>No completed transactions yet</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {jobs.filter(j => j.status === 'completed').map((job) => (
                        <div key={job.id} className="py-4 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{job.title}</p>
                            <p className="text-sm text-stone-500">{job.client_name}</p>
                          </div>
                          <span className="text-green-600 font-bold">
                            +${(user?.hourly_rate || 200) * 8}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Membership & Visibility Section */}
            {activeSection === 'membership' && (
              <div className="space-y-8" data-testid="membership-section">
                <div>
                  <h2 className="text-xl font-bold">Membership & Visibility</h2>
                  <p className="text-stone-500 text-sm mt-1">Manage your subscription plan and boost your profile visibility</p>
                </div>

                {subscriptionLoading && !subscription ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
                  </div>
                ) : (
                  <>
                    {/* Current Plan Banner */}
                    <div className="bg-gradient-to-r from-stone-900 to-stone-700 text-white rounded-3xl p-8" data-testid="current-plan-banner">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-widest text-stone-300 mb-2">Current Plan</div>
                          <div className="text-3xl font-bold serif italic flex items-center gap-3">
                            {subscription?.plan_details?.name || 'Standard'}
                            {subscription?.plan === 'premium' && <Sparkles className="w-6 h-6 text-yellow-400" />}
                            {subscription?.plan === 'elite' && <Crown className="w-6 h-6 text-yellow-400" />}
                          </div>
                          <div className="mt-3 text-stone-300 text-sm">
                            {subscription?.plan === 'standard' && 'Free tier with basic features'}
                            {subscription?.plan === 'premium' && '$29/month • Pro features unlocked'}
                            {subscription?.plan === 'elite' && '$99/month • All premium features + priority visibility'}
                          </div>
                        </div>
                        {subscription?.is_boosted && (
                          <div className="bg-yellow-400 text-stone-900 px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            SPOTLIGHT ACTIVE
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Plan Comparison */}
                    <div>
                      <h3 className="font-bold mb-4">Choose Your Plan</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Standard Plan */}
                        <div className={`bg-white rounded-3xl border-2 p-6 transition-all ${
                          subscription?.plan === 'standard' ? 'border-stone-900 shadow-lg' : 'border-stone-200 hover:border-stone-300'
                        }`} data-testid="plan-standard">
                          <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">Standard</div>
                          <div className="text-3xl font-bold serif italic mb-4">Free</div>
                          <ul className="space-y-3 mb-6 text-sm text-stone-600">
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> 10 Portfolio Images
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> 20% Marketplace Fee
                            </li>
                            <li className="flex items-center gap-2">
                              <X className="w-4 h-4 text-stone-300" /> No Monthly Boosts
                            </li>
                            <li className="flex items-center gap-2">
                              <X className="w-4 h-4 text-stone-300" /> No Verified Badge
                            </li>
                          </ul>
                          <button
                            onClick={() => handleChangePlan('standard')}
                            disabled={subscription?.plan === 'standard' || subscriptionLoading}
                            className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                              subscription?.plan === 'standard'
                                ? 'bg-stone-100 text-stone-400 cursor-default'
                                : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                            }`}
                            data-testid="select-standard-btn"
                          >
                            {subscription?.plan === 'standard' ? 'Current Plan' : 'Downgrade'}
                          </button>
                        </div>

                        {/* Premium Plan */}
                        <div className={`bg-white rounded-3xl border-2 p-6 relative transition-all ${
                          subscription?.plan === 'premium' ? 'border-stone-900 shadow-lg' : 'border-stone-200 hover:border-stone-300'
                        }`} data-testid="plan-premium">
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-stone-900 text-white px-4 py-1 rounded-full text-[10px] font-bold tracking-widest">
                            POPULAR
                          </div>
                          <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2 flex items-center gap-2">
                            Premium <Sparkles className="w-3 h-3 text-yellow-500" />
                          </div>
                          <div className="text-3xl font-bold serif italic mb-4">$29<span className="text-base font-normal text-stone-400">/mo</span></div>
                          <ul className="space-y-3 mb-6 text-sm text-stone-600">
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> Unlimited Portfolio
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> 10% Marketplace Fee
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> 2 Profile Boosts/mo
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> Verified Pro Badge
                            </li>
                          </ul>
                          <button
                            onClick={() => handleChangePlan('premium')}
                            disabled={subscription?.plan === 'premium' || subscriptionLoading}
                            className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                              subscription?.plan === 'premium'
                                ? 'bg-stone-100 text-stone-400 cursor-default'
                                : 'bg-stone-900 text-white hover:bg-stone-700'
                            }`}
                            data-testid="select-premium-btn"
                          >
                            {subscription?.plan === 'premium' ? 'Current Plan' : subscription?.plan === 'elite' ? 'Downgrade' : 'Upgrade'}
                          </button>
                        </div>

                        {/* Elite Plan */}
                        <div className={`bg-white rounded-3xl border-2 p-6 transition-all ${
                          subscription?.plan === 'elite' ? 'border-stone-900 shadow-lg' : 'border-stone-200 hover:border-stone-300'
                        }`} data-testid="plan-elite">
                          <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2 flex items-center gap-2">
                            Elite <Crown className="w-3 h-3 text-yellow-500" />
                          </div>
                          <div className="text-3xl font-bold serif italic mb-4">$99<span className="text-base font-normal text-stone-400">/mo</span></div>
                          <ul className="space-y-3 mb-6 text-sm text-stone-600">
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> 0% Transaction Fees
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> Weekly Spotlight (Auto)
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> Priority Brand Matching
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" /> Custom aura.pro/url
                            </li>
                          </ul>
                          <button
                            onClick={() => handleChangePlan('elite')}
                            disabled={subscription?.plan === 'elite' || subscriptionLoading}
                            className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                              subscription?.plan === 'elite'
                                ? 'bg-stone-100 text-stone-400 cursor-default'
                                : 'bg-stone-900 text-white hover:bg-stone-700'
                            }`}
                            data-testid="select-elite-btn"
                          >
                            {subscription?.plan === 'elite' ? 'Current Plan' : 'Upgrade'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Aura Spotlight Section */}
                    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl border border-yellow-200 p-8" data-testid="spotlight-section">
                      <div className="flex items-start gap-6">
                        <div className="w-16 h-16 rounded-2xl bg-yellow-400 flex items-center justify-center flex-shrink-0">
                          <Zap className="w-8 h-8 text-stone-900" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-2">Aura Spotlight</h3>
                          <p className="text-stone-600 mb-4">
                            Boost your profile to the top of search results for 48 hours. Get 3x more visibility and inquiries.
                          </p>
                          
                          <div className="flex items-center gap-4 mb-6">
                            <div className="bg-white px-4 py-2 rounded-xl border border-yellow-200">
                              <span className="text-2xl font-bold serif italic">{subscription?.boosts_remaining || 0}</span>
                              <span className="text-stone-500 text-sm ml-2">boosts available</span>
                            </div>
                            {subscription?.is_boosted && (
                              <div className="text-green-600 text-sm font-medium flex items-center gap-2">
                                <CheckCircle className="w-4 h-4" />
                                Spotlight active until {subscription?.boost_expires_at ? new Date(subscription.boost_expires_at).toLocaleDateString() : 'N/A'}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-3">
                            <button
                              onClick={handleActivateBoost}
                              disabled={subscriptionLoading || subscription?.is_boosted || (subscription?.boosts_remaining || 0) <= 0}
                              className={`px-6 py-3 rounded-xl font-bold text-sm transition flex items-center gap-2 ${
                                subscription?.is_boosted || (subscription?.boosts_remaining || 0) <= 0
                                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                                  : 'bg-yellow-400 text-stone-900 hover:bg-yellow-300'
                              }`}
                              data-testid="activate-boost-btn"
                            >
                              <Zap className="w-4 h-4" />
                              {subscription?.is_boosted ? 'Spotlight Active' : 'Activate Spotlight'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Buy More Boosts */}
                    <div className="bg-white rounded-3xl border border-stone-200 p-8" data-testid="buy-boosts-section">
                      <h3 className="font-bold mb-6">Need More Spotlights?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <button
                          onClick={() => handlePurchaseBoosts('single')}
                          disabled={subscriptionLoading}
                          className="p-4 border-2 border-stone-200 rounded-2xl hover:border-stone-400 transition text-left group"
                          data-testid="buy-single-boost"
                        >
	                          <div className="text-xl font-bold serif italic group-hover:text-stone-900">1 Boost</div>
	                          <div className="text-stone-500 text-sm">{formatBoostPrice(boostPricing.single)}</div>
	                        </button>
                        <button
                          onClick={() => handlePurchaseBoosts('pack3')}
                          disabled={subscriptionLoading}
                          className="p-4 border-2 border-stone-200 rounded-2xl hover:border-stone-400 transition text-left group relative"
                          data-testid="buy-pack3-boost"
                        >
	                          <div className="absolute -top-2 right-4 bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">SAVE 10%</div>
	                          <div className="text-xl font-bold serif italic group-hover:text-stone-900">3 Boosts</div>
	                          <div className="text-stone-500 text-sm">{formatBoostPrice(boostPricing.pack3)}</div>
	                        </button>
                        <button
                          onClick={() => handlePurchaseBoosts('pack5')}
                          disabled={subscriptionLoading}
                          className="p-4 border-2 border-stone-200 rounded-2xl hover:border-stone-400 transition text-left group relative"
                          data-testid="buy-pack5-boost"
                        >
	                          <div className="absolute -top-2 right-4 bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">SAVE 20%</div>
	                          <div className="text-xl font-bold serif italic group-hover:text-stone-900">5 Boosts</div>
	                          <div className="text-stone-500 text-sm">{formatBoostPrice(boostPricing.pack5)}</div>
	                        </button>
                      </div>
                      <p className="text-stone-400 text-xs mt-4 text-center">
                        * Boosts are valid for 90 days from purchase
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Chat Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeChat}>
          <div 
            className="bg-white rounded-3xl w-full max-w-lg h-[600px] flex flex-col overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Chat Header */}
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-stone-100 rounded-2xl flex items-center justify-center font-bold text-stone-400">
                  {getInitials(selectedConversation.client_name)}
                </div>
                <div>
                  <h3 className="font-bold">{selectedConversation.client_name}</h3>
                  <p className="text-xs text-stone-400">Client</p>
                </div>
              </div>
              <button 
                onClick={closeChat}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition"
                data-testid="close-chat-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {chatMessages.length === 0 ? (
                <div className="text-center text-stone-400 py-12">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-stone-200" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.sender_type === 'photographer' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                        msg.sender_type === 'photographer' 
                          ? 'bg-stone-900 text-white rounded-br-md' 
                          : 'bg-stone-100 text-stone-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {renderMessageContent(msg.content, msg.sender_type === 'photographer' ? 'underline underline-offset-2 text-white' : 'underline underline-offset-2 text-stone-900')}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-stone-100">
              <div className="flex items-center space-x-3">
                <input
	                  type="text"
	                  value={newMessage}
	                  onChange={(e) => setNewMessage(e.target.value)}
	                  onKeyDown={(e) => {
	                    if (e.key === 'Enter') {
	                      e.preventDefault();
	                      sendChatMessage();
	                    }
	                  }}
	                  placeholder="Type a message..."
                  className="flex-1 px-4 py-3 bg-stone-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="w-12 h-12 bg-stone-900 text-white rounded-xl flex items-center justify-center hover:bg-stone-700 disabled:opacity-50 transition"
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Delivery Modal */}
      {deliveryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeDeliveryModal}>
          <div 
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">
                  {deliveryModal.status === 'completed' ? 'Manage Client Gallery' : deliveryModal.status === 'approvedForFinalDelivery' ? 'Send Final Gallery' : 'Send Preview Gallery'}
                </h3>
                <p className="text-sm text-stone-500 mt-1">
                  Job: {deliveryModal.title || deliveryModal.description?.slice(0, 50)}
                </p>
              </div>
              <button 
                onClick={closeDeliveryModal}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition"
                data-testid="close-delivery-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Upload Section */}
              <div className="bg-stone-50 rounded-2xl p-6">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Add Images
                </h4>
                
                {/* File Upload */}
                <input
                  type="file"
                  ref={deliveryInputRef}
                  onChange={handleDeliveryUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => deliveryInputRef.current?.click()}
                  disabled={deliveryUploading}
                  className="w-full py-4 border-2 border-dashed border-stone-300 rounded-xl hover:border-stone-400 hover:bg-white transition flex items-center justify-center gap-2 text-stone-500"
                  data-testid="upload-delivery-btn"
                >
                  {deliveryUploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Click to upload image
                    </>
                  )}
                </button>

                {/* URL Input */}
                <div className="mt-4">
                  <p className="text-xs text-stone-400 mb-2">Or add image URL</p>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newDeliveryUrl}
                      onChange={(e) => setNewDeliveryUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="flex-1 px-4 py-2 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200"
                      data-testid="delivery-url-input"
                    />
                    <button
                      onClick={addDeliveryImageUrl}
                      disabled={!newDeliveryUrl.trim()}
                      className="px-4 py-2 bg-stone-900 text-white rounded-xl font-medium hover:bg-stone-700 disabled:opacity-50"
                      data-testid="add-url-btn"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Image Preview Grid */}
              <div>
                <h4 className="font-bold mb-4">
                  Images to Deliver ({deliveryImages.length})
                </h4>
                {deliveryImages.length === 0 ? (
                  <div className="bg-stone-50 rounded-2xl p-12 text-center">
                    <Image className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400">No images added yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-3">
                    {deliveryImages.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img 
                          src={getImageUrl(img)} 
                          alt={`Delivery ${idx + 1}`} 
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <button
                          onClick={() => removeDeliveryImage(idx)}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          data-testid={`remove-image-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-stone-100 flex justify-end gap-3">
              <button
                onClick={closeDeliveryModal}
                className="px-6 py-3 text-stone-600 font-medium hover:bg-stone-100 rounded-xl transition"
              >
                Cancel
              </button>
              {deliveryModal.status === 'completed' ? (
                <button
                  onClick={addMoreImages}
                  disabled={deliveryImages.length === 0}
                  className="px-6 py-3 bg-stone-900 text-white font-bold rounded-xl hover:bg-stone-700 disabled:opacity-50 transition flex items-center gap-2"
                  data-testid="update-gallery-btn"
                >
                  <Check className="w-5 h-5" />
                  Update Gallery
                </button>
              ) : (
                <button
                  onClick={submitDelivery}
                  disabled={deliveryImages.length === 0}
                  className="px-6 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 disabled:opacity-50 transition flex items-center gap-2"
                  data-testid="deliver-images-btn"
                >
                  <Check className="w-5 h-5" />
                  {deliveryModal.status === 'approvedForFinalDelivery' ? 'Deliver Final Gallery' : 'Send Preview'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Share Modal */}
      {shareGallery && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setShareGallery(null)}>
          <div
            className="bg-white rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-2">Private Gallery Ready</p>
                <h3 className="text-2xl font-bold serif italic">{shareGallery.title || 'Client Gallery'}</h3>
                <p className="text-sm text-stone-500 mt-1">
                  For {shareGallery.client_name || shareGallery.client_email}
                </p>
              </div>
              <button
                onClick={() => setShareGallery(null)}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition"
                data-testid="close-share-gallery-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-stone-100">
                {shareGallery.delivered_images?.[0] ? (
                  <img
                    src={getImageUrl(shareGallery.delivered_images[0])}
                    alt={shareGallery.title || 'Gallery preview'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Images className="w-12 h-12 text-stone-300" />
                  </div>
                )}
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-bold">
                  {shareGallery.delivered_images?.length || 0} images
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 p-2">
                <Lock className="w-4 h-4 text-stone-400 shrink-0 ml-2" />
                <input
                  value={getGalleryShareUrl(shareGallery.id)}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-stone-600 focus:outline-none min-w-0"
                  data-testid="gallery-share-link"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={() => copyGalleryLink(shareGallery)}
                  disabled={copyingGalleryId === shareGallery.id}
                  className="px-4 py-3 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-50"
                  data-testid="copy-share-link-btn"
                >
                  <Copy className="w-4 h-4" />
                  <span>{copyingGalleryId === shareGallery.id ? 'Copied' : 'Copy Link'}</span>
                </button>
                <Link
                  to={`/gallery/${shareGallery.id}`}
                  className="px-4 py-3 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm font-bold flex items-center justify-center gap-2 transition"
                  data-testid="open-share-gallery-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Open Gallery</span>
                </Link>
                <button
                  onClick={() => sendGalleryLink(shareGallery)}
                  disabled={sharingGalleryId === shareGallery.id}
                  className="px-4 py-3 rounded-xl bg-stone-900 hover:bg-stone-700 text-white text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-50"
                  data-testid="send-share-link-btn"
                >
                  {sharingGalleryId === shareGallery.id ? (
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>Send Link</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Gallery Modal */}
      {showCreateGallery && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeCreateGallery}>
          <div 
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-stone-100 flex items-center justify-between bg-gradient-to-r from-stone-50 to-white">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Images className="w-6 h-6" />
                  Create Private Gallery
                </h3>
                <p className="text-sm text-stone-500 mt-1">Share images with your client</p>
              </div>
              <button 
                onClick={closeCreateGallery}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition"
                data-testid="close-gallery-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Gallery Details */}
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 block mb-2">
                    Gallery Title *
                  </label>
                  <input
                    type="text"
                    value={galleryForm.title}
                    onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                    placeholder="e.g., Elena & Marc Wedding"
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200"
                    data-testid="gallery-title-input"
                  />
                </div>
                
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 block mb-2">
                    Description
                  </label>
                  <textarea
                    value={galleryForm.description}
                    onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                    placeholder="A beautiful collection of moments captured at your special day..."
                    rows={3}
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200 resize-none"
                    data-testid="gallery-description-input"
                  />
                </div>
              </div>

              {/* Client Selection */}
              <div className="bg-stone-50 rounded-2xl p-5 space-y-4">
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 block">
                  Client Details *
                </label>
                
                {clients.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-stone-500 mb-2">Select existing client:</p>
                    <div className="flex flex-wrap gap-2">
                      {clients.map((client) => (
                        <button
                          key={client.id}
                          onClick={() => setGalleryForm({ 
                            ...galleryForm, 
                            client_email: client.email,
                            client_name: client.name 
                          })}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                            galleryForm.client_email === client.email
                              ? 'bg-stone-900 text-white'
                              : 'bg-white border border-stone-200 hover:border-stone-400'
                          }`}
                        >
                          {client.name}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-stone-400 mt-3 text-center">— or enter new client —</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 block mb-2">
                      Client Name
                    </label>
                    <input
                      type="text"
                      value={galleryForm.client_name}
                      onChange={(e) => setGalleryForm({ ...galleryForm, client_name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200 bg-white"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 block mb-2">
                      Client Email *
                    </label>
                    <input
                      type="email"
                      value={galleryForm.client_email}
                      onChange={(e) => setGalleryForm({ ...galleryForm, client_email: e.target.value })}
                      placeholder="client@email.com"
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-200 bg-white"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 block mb-3">
                  Gallery Images *
                </label>
                
                <input
                  type="file"
                  ref={galleryInputRef}
                  onChange={handleGalleryImageUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={galleryUploading}
                  className="w-full py-6 border-2 border-dashed border-stone-300 rounded-2xl hover:border-stone-400 hover:bg-stone-50 transition flex flex-col items-center justify-center gap-2 text-stone-500"
                  data-testid="upload-gallery-images-btn"
                >
                  {galleryUploading ? (
                    <>
                      <div className="w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin"></div>
                      <span className="text-sm font-medium">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8" />
                      <span className="text-sm font-medium">Click to upload images</span>
                      <span className="text-xs text-stone-400">Supports multiple files</span>
                    </>
                  )}
                </button>

                {/* Image Preview Grid */}
                {galleryImages.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-stone-500 mb-3">{galleryImages.length} images added</p>
                    <div className="grid grid-cols-5 gap-2">
                      {galleryImages.map((img, idx) => (
                        <div key={idx} className="relative group aspect-square">
                          <img 
                            src={getImageUrl(img)} 
                            alt={`Gallery ${idx + 1}`} 
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removeGalleryImage(idx)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                            data-testid={`remove-gallery-image-${idx}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-stone-100 flex justify-between items-center bg-stone-50">
              <p className="text-xs text-stone-400">
                Client will receive access to view this gallery
              </p>
              <div className="flex gap-3">
                <button
                  onClick={closeCreateGallery}
                  className="px-6 py-3 text-stone-600 font-medium hover:bg-stone-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={submitGallery}
                  disabled={gallerySubmitting || !galleryForm.title || !galleryForm.client_email || galleryImages.length === 0}
                  className="px-6 py-3 bg-stone-900 text-white font-bold rounded-xl hover:bg-stone-700 disabled:opacity-50 transition flex items-center gap-2"
                  data-testid="submit-gallery-btn"
                >
                  {gallerySubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Create Gallery
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Gallery Modal */}
      {editingGallery && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeEditGallery}>
          <div 
            className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">{editingGallery.title}</h3>
                <p className="text-sm text-stone-500 mt-1">
                  For: {editingGallery.client_name || editingGallery.client_email}
                </p>
              </div>
              <button 
                onClick={closeEditGallery}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Upload Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold">Gallery Images ({editGalleryImages.length})</h4>
                  <input
                    type="file"
                    ref={editGalleryInputRef}
                    onChange={handleEditGalleryUpload}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <button
                    onClick={() => editGalleryInputRef.current?.click()}
                    disabled={editGalleryUploading}
                    className="px-4 py-2 bg-stone-100 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-stone-200 transition disabled:opacity-50"
                  >
                    {editGalleryUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Add Images</span>
                      </>
                    )}
                  </button>
                </div>
                
                {editGalleryImages.length === 0 ? (
                  <div className="py-12 text-center bg-stone-50 rounded-2xl">
                    <Images className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400">No images in this gallery</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                    {editGalleryImages.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img 
                          src={getImageUrl(img)} 
                          alt={`Image ${idx + 1}`} 
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <button
                          onClick={() => removeEditGalleryImage(idx)}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-lg"
                          title="Remove image"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition">
                          #{idx + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Gallery Info */}
              <div className="bg-stone-50 rounded-2xl p-5 space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-widest text-stone-400">Gallery Details</h4>
                {editingGallery.description && (
                  <p className="text-sm text-stone-600">{editingGallery.description}</p>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-stone-400">Client:</span>
                    <span className="ml-2 font-medium">{editingGallery.client_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-stone-400">Email:</span>
                    <span className="ml-2 font-medium">{editingGallery.client_email}</span>
                  </div>
                  <div>
                    <span className="text-stone-400">Created:</span>
                    <span className="ml-2 font-medium">
                      {editingGallery.created_at ? new Date(editingGallery.created_at).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <span className="text-stone-400">Status:</span>
                    <span className="ml-2 font-medium capitalize">{editingGallery.status}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-stone-100 flex justify-between items-center">
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete this gallery?')) {
                    deleteGallery(editingGallery.id);
                    closeEditGallery();
                  }
                }}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl text-sm font-medium transition flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Delete Gallery</span>
              </button>
              <div className="flex gap-3">
                <button
                  onClick={closeEditGallery}
                  className="px-6 py-3 text-stone-600 font-medium hover:bg-stone-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={saveGalleryChanges}
                  disabled={editGalleryImages.length === 0}
                  className="px-6 py-3 bg-stone-900 text-white font-bold rounded-xl hover:bg-stone-700 disabled:opacity-50 transition flex items-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const { user, token, isAuthenticated, userType } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('crm');
  const [loading, setLoading] = useState(true);
  const [photographers, setPhotographers] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteConfig, setSiteConfig] = useState({
    hero_title: 'Capturing the soul of the moment.',
    hero_accent: 'soul',
    hero_image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4',
    boost_price_single: 9.99,
    boost_price_pack3: 26.99,
    boost_price_pack5: 39.99,
    premium_monthly_boosts: 2,
    elite_monthly_boosts: 4,
    categories: ['Editorial', 'Wedding', 'Fashion', 'Portrait']
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [photographersData, statsData, configData] = await Promise.all([
        api.getAdminPhotographers(token),
        api.getAdminStats(token),
        api.getSiteConfig()
      ]);
      setPhotographers(photographersData);
      setStats(statsData);
      setSiteConfig((currentConfig) => ({ ...currentConfig, ...configData }));
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 403) {
        navigate('/pro');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (userType !== 'photographer') {
      navigate('/dashboard');
      return;
    }
    if (!user?.is_admin) {
      navigate('/pro');
      return;
    }
    loadData();
  }, [isAuthenticated, userType, user?.is_admin, navigate, loadData]);

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.updateSiteConfig(siteConfig, token);
      showNotification('Site configuration updated successfully!');
    } catch (error) {
      showNotification('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAdmin = async (photographerId, currentIsAdmin) => {
    try {
      if (currentIsAdmin) {
        await api.revokeAdmin(photographerId, token);
      } else {
        await api.makeAdmin(photographerId, token);
      }
      loadData();
      showNotification(currentIsAdmin ? 'Admin access revoked' : 'Admin access granted');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const filteredPhotographers = photographers.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTierBadge = (plan) => {
    switch (plan) {
      case 'elite':
        return <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded font-bold uppercase tracking-[0.12em]">Elite</span>;
      case 'premium':
        return <span className="text-[10px] bg-stone-700 text-stone-300 px-2 py-0.5 rounded font-bold uppercase tracking-[0.12em]">Premium</span>;
      default:
        return <span className="text-[10px] bg-stone-800 text-stone-500 px-2 py-0.5 rounded font-bold uppercase tracking-[0.12em]">Standard</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0c0a09] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c0a09] text-stone-200">
      {/* Notification */}
      {notification && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[100]">
          <div className="bg-white text-black px-6 py-3 rounded-full font-bold text-sm shadow-2xl flex items-center space-x-3">
            <CheckCircle className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        </div>
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-64 bg-[#1c1917] border-r border-stone-800 hidden lg:flex flex-col fixed h-full">
          <div className="p-8">
            <div className="text-xl font-bold serif italic mb-10 text-white">
              AURA <span className="text-[10px] bg-stone-700 px-2 py-0.5 rounded ml-2 not-italic tracking-normal">CMS</span>
            </div>
            
            <nav className="space-y-1">
              <button 
                onClick={() => setActiveTab('crm')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium transition ${
                  activeTab === 'crm' 
                    ? 'bg-stone-800 text-stone-100 border-l-4 border-stone-100' 
                    : 'text-stone-500 hover:text-stone-200'
                }`}
                data-testid="admin-nav-crm"
              >
                <User className="w-5 h-5" />
                <span>Photographers CRM</span>
              </button>
              <button 
                onClick={() => setActiveTab('cms')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium transition ${
                  activeTab === 'cms' 
                    ? 'bg-stone-800 text-stone-100 border-l-4 border-stone-100' 
                    : 'text-stone-500 hover:text-stone-200'
                }`}
                data-testid="admin-nav-cms"
              >
                <Sparkles className="w-5 h-5" />
                <span>Site Engine</span>
              </button>
              <button 
                onClick={() => setActiveTab('revenue')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium transition ${
                  activeTab === 'revenue' 
                    ? 'bg-stone-800 text-stone-100 border-l-4 border-stone-100' 
                    : 'text-stone-500 hover:text-stone-200'
                }`}
                data-testid="admin-nav-revenue"
              >
                <DollarSign className="w-5 h-5" />
                <span>Revenue & Boosts</span>
              </button>
            </nav>
          </div>

          <div className="mt-auto p-8 border-t border-stone-800">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center text-[10px] font-bold">
                {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'AD'}
              </div>
              <div>
                <div className="text-xs font-bold text-white">{user?.name}</div>
                <div className="text-[10px] text-stone-500">Admin</div>
              </div>
            </div>
            <button 
              onClick={() => navigate('/pro')}
              className="mt-4 text-xs text-stone-500 hover:text-white flex items-center"
            >
              <ArrowRight className="w-3 h-3 mr-1" /> Back to Dashboard
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 p-8">
          {/* CRM Tab */}
          {activeTab === 'crm' && (
            <div data-testid="admin-tab-crm">
              <header className="flex justify-between items-center mb-10">
                <div>
                  <h1 className="text-3xl font-bold text-white serif">Photographer CRM</h1>
                  <p className="text-stone-500 text-sm">Managing relationships, vetting & availability.</p>
                </div>
                <div className="flex space-x-3">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 w-4 h-4" />
                    <input 
                      type="text" 
                      placeholder="Search artists..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-stone-900 border border-stone-800 rounded-xl py-2 pl-10 pr-4 text-xs focus:outline-none focus:border-stone-600 text-white"
                      data-testid="admin-search"
                    />
                  </div>
                </div>
              </header>

              <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                <div className="xl:col-span-3 space-y-6">
                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-5 rounded-2xl">
                      <div className="text-[10px] font-bold text-stone-500 uppercase mb-1">Total Artists</div>
                      <div className="text-2xl font-bold text-white">{stats?.photographers?.total || 0}</div>
                    </div>
                    <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-5 rounded-2xl">
                      <div className="text-[10px] font-bold text-stone-500 uppercase mb-1">Premium Tier</div>
                      <div className="text-2xl font-bold text-blue-400">{stats?.photographers?.premium || 0}</div>
                    </div>
                    <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-5 rounded-2xl">
                      <div className="text-[10px] font-bold text-stone-500 uppercase mb-1">Elite Tier</div>
                      <div className="text-2xl font-bold text-purple-400">{stats?.photographers?.elite || 0}</div>
                    </div>
                  </div>

                  {/* CRM Table */}
                  <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 rounded-3xl overflow-hidden">
                    <table className="w-full text-left">
                      <thead className="text-[10px] font-bold text-stone-500 uppercase tracking-widest bg-stone-900/30">
                        <tr>
                          <th className="px-6 py-4">Photographer</th>
                          <th className="px-6 py-4">Tier / Plan</th>
                          <th className="px-6 py-4">Availability</th>
                          <th className="px-6 py-4">Bookings</th>
                          <th className="px-6 py-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-800/50">
                        {filteredPhotographers.map((p) => (
                          <tr key={p.id} className="hover:bg-white/5 transition group">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <img src={p.img} alt={p.name} className="w-10 h-10 rounded-full object-cover" />
                                <div>
                                  <div className="text-sm font-bold text-white flex items-center gap-2">
                                    {p.name}
                                    {p.is_admin && <span className="text-[8px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">ADMIN</span>}
                                  </div>
                                  <div className="text-[10px] text-stone-500">{p.email} • {p.location}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {getTierBadge(p.subscription?.plan)}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center text-xs">
                                <span className={`w-2 h-2 rounded-full mr-2 ${p.available_slots > 0 ? 'bg-green-500' : 'bg-stone-700'}`}></span>
                                {p.available_slots || 0} slots open
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs font-mono">{p.total_bookings || 0} Total</td>
                            <td className="px-6 py-4 text-right">
                              <button 
                                onClick={() => navigate(`/photographer/${p.id}`)}
                                className="text-stone-500 hover:text-white mr-3"
                                title="View Profile"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleToggleAdmin(p.id, p.is_admin)}
                                className={`text-stone-500 hover:text-white ${p.is_admin ? 'text-yellow-400' : ''}`}
                                title={p.is_admin ? 'Revoke Admin' : 'Make Admin'}
                              >
                                <Crown className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-6 rounded-3xl h-fit space-y-4">
                  <h3 className="font-bold mb-4 flex items-center">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Quick Stats
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-400">Total Clients</span>
                      <span className="font-bold text-white">{stats?.clients?.total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-400">Total Bookings</span>
                      <span className="font-bold text-white">{stats?.bookings?.total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-400">Paid Bookings</span>
                      <span className="font-bold text-green-400">{stats?.bookings?.paid || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CMS Tab */}
          {activeTab === 'cms' && (
            <div data-testid="admin-tab-cms">
              <header className="flex justify-between items-center mb-10">
                <div>
                  <h1 className="text-3xl font-bold text-white serif">Site Engine</h1>
                  <p className="text-stone-500 text-sm">Real-time content and visual configuration.</p>
                </div>
                <button 
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="bg-white text-black px-6 py-2.5 rounded-xl text-sm font-bold flex items-center shadow-lg hover:bg-stone-100 disabled:opacity-50"
                  data-testid="admin-save-config"
                >
                  <Upload className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Publish Changes'}
                </button>
              </header>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Config Form */}
                <div className="space-y-8">
                  <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-8 rounded-[2rem]">
                    <h3 className="text-lg font-bold mb-6 flex items-center">
                      <MapPin className="w-5 h-5 mr-3 text-stone-500" /> Hero Section
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Hero Title (Main)</label>
                        <input 
                          type="text" 
                          value={siteConfig.hero_title}
                          onChange={(e) => setSiteConfig({ ...siteConfig, hero_title: e.target.value })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                          data-testid="cms-hero-title"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Hero Accent Word (Italic)</label>
                        <input 
                          type="text" 
                          value={siteConfig.hero_accent}
                          onChange={(e) => setSiteConfig({ ...siteConfig, hero_accent: e.target.value })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                          data-testid="cms-hero-accent"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Featured Hero Image URL</label>
                        <input 
                          type="text" 
                          value={siteConfig.hero_image}
                          onChange={(e) => setSiteConfig({ ...siteConfig, hero_image: e.target.value })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                          data-testid="cms-hero-image"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-8 rounded-[2rem]">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-bold flex items-center">
                        <Zap className="w-5 h-5 mr-3 text-stone-500" /> Spotlight Settings
                      </h3>
                      <span className="text-[10px] text-green-500 font-bold uppercase tracking-widest">Active System</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Single Boost Price ($)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          value={siteConfig.boost_price_single}
                          onChange={(e) => setSiteConfig({ ...siteConfig, boost_price_single: parseFloat(e.target.value) })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Pack (3) Price ($)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          value={siteConfig.boost_price_pack3}
                          onChange={(e) => setSiteConfig({ ...siteConfig, boost_price_pack3: parseFloat(e.target.value) })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Pack (5) Price ($)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          value={siteConfig.boost_price_pack5}
                          onChange={(e) => setSiteConfig({ ...siteConfig, boost_price_pack5: parseFloat(e.target.value) })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-stone-500 mb-2 block">Premium Monthly Boosts</label>
                        <input 
                          type="number" 
                          value={siteConfig.premium_monthly_boosts}
                          onChange={(e) => setSiteConfig({ ...siteConfig, premium_monthly_boosts: parseInt(e.target.value) })}
                          className="w-full bg-[#1c1917] border border-stone-700 text-white px-4 py-3 rounded-xl text-sm focus:border-stone-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-8 rounded-[2rem]">
                    <h3 className="text-lg font-bold mb-6 flex items-center">
                      <Star className="w-5 h-5 mr-3 text-stone-500" /> Categories
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {siteConfig.categories.map((cat, idx) => (
                        <span key={idx} className="bg-stone-800 px-3 py-1.5 rounded-lg text-xs flex items-center">
                          {cat}
                          <button 
                            onClick={() => setSiteConfig({
                              ...siteConfig,
                              categories: siteConfig.categories.filter((_, i) => i !== idx)
                            })}
                            className="ml-2 hover:text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <button 
                        onClick={() => {
                          const newCat = prompt('Enter new category:');
                          if (newCat) {
                            setSiteConfig({
                              ...siteConfig,
                              categories: [...siteConfig.categories, newCat]
                            });
                          }
                        }}
                        className="border border-dashed border-stone-600 px-3 py-1.5 rounded-lg text-xs hover:bg-stone-800 transition"
                      >
                        + Add Tag
                      </button>
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="sticky top-8">
                  <div className="text-[10px] font-bold uppercase text-stone-500 mb-4 tracking-widest flex justify-between">
                    <span>Live Canvas Preview</span>
                    <span className="text-green-500 flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 animate-pulse"></span> Syncing
                    </span>
                  </div>
                  <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden scale-90 origin-top">
                    <div className="p-6 border-b border-stone-100 flex justify-between">
                      <div className="font-bold serif italic text-stone-900">AURA</div>
                      <div className="flex space-x-2">
                        <div className="w-4 h-4 rounded-full bg-stone-100"></div>
                        <div className="w-4 h-4 rounded-full bg-stone-100"></div>
                      </div>
                    </div>
                    <div className="p-10 text-center">
                      <h1 className="text-2xl serif text-stone-900 mb-4">
                        {siteConfig.hero_title.split(siteConfig.hero_accent).map((part, i, arr) => (
                          <span key={i}>
                            {part}
                            {i < arr.length - 1 && <span className="italic text-stone-400">{siteConfig.hero_accent}</span>}
                          </span>
                        ))}
                      </h1>
                      <div className="aspect-video bg-stone-200 rounded-2xl overflow-hidden">
                        <img 
                          src={`${siteConfig.hero_image}?auto=format&fit=crop&q=80&w=400`} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="p-6 bg-stone-50 flex space-x-2">
                      {siteConfig.categories.slice(0, 4).map((cat, i) => (
                        <div key={i} className="px-3 py-1 bg-stone-200 rounded text-xs">{cat}</div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl text-[11px] text-blue-400 leading-relaxed">
                    <Bell className="w-3 h-3 inline mr-1" /> Changes are saved to the platform's global state. Click "Publish" to apply updates.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Revenue Tab */}
          {activeTab === 'revenue' && (
            <div data-testid="admin-tab-revenue">
              <header className="mb-10">
                <h1 className="text-3xl font-bold text-white serif">Revenue & Performance</h1>
                <p className="text-stone-500 text-sm">Subscription growth and Aura Spotlight analytics.</p>
              </header>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                  <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-8 rounded-3xl">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-6">Revenue Overview</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="p-6 bg-stone-800/50 rounded-2xl">
                        <div className="text-[10px] font-bold text-stone-500 uppercase mb-2">Total Booking Revenue</div>
                        <div className="text-3xl font-bold text-white">${stats?.revenue?.booking_deposits?.toLocaleString() || '0'}</div>
                        <p className="text-green-500 text-xs flex items-center mt-2">
                          <ArrowUp className="w-3 h-3 mr-1" /> From paid deposits
                        </p>
                      </div>
                      <div className="p-6 bg-stone-800/50 rounded-2xl">
                        <div className="text-[10px] font-bold text-stone-500 uppercase mb-2">Spotlight Sales</div>
                        <div className="text-3xl font-bold text-white">${stats?.revenue?.boost_sales || '0'}</div>
                        <p className="text-stone-400 text-xs mt-2">
                          {stats?.revenue?.boost_count || 0} boosts sold
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-8 rounded-3xl">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-6">Tier Distribution</h3>
                    <div className="flex items-end space-x-4 h-48">
                      <div className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-stone-700 rounded-t-lg transition-all duration-500"
                          style={{ height: `${(stats?.photographers?.standard || 0) / (stats?.photographers?.total || 1) * 100}%`, minHeight: '20px' }}
                        ></div>
                        <span className="text-[10px] text-stone-500 mt-2">Standard</span>
                        <span className="text-sm font-bold">{stats?.photographers?.standard || 0}</span>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-blue-500 rounded-t-lg transition-all duration-500"
                          style={{ height: `${(stats?.photographers?.premium || 0) / (stats?.photographers?.total || 1) * 100}%`, minHeight: '20px' }}
                        ></div>
                        <span className="text-[10px] text-stone-500 mt-2">Premium</span>
                        <span className="text-sm font-bold">{stats?.photographers?.premium || 0}</span>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-purple-500 rounded-t-lg transition-all duration-500"
                          style={{ height: `${(stats?.photographers?.elite || 0) / (stats?.photographers?.total || 1) * 100}%`, minHeight: '20px' }}
                        ></div>
                        <span className="text-[10px] text-stone-500 mt-2">Elite</span>
                        <span className="text-sm font-bold">{stats?.photographers?.elite || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-900/40 backdrop-blur-sm border border-stone-800/50 p-8 rounded-3xl h-fit">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4">Key Metrics</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-stone-800/30 rounded-xl">
                      <span className="text-xs text-stone-400">Paid Bookings</span>
                      <span className="text-lg font-bold text-green-400">{stats?.bookings?.paid || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-stone-800/30 rounded-xl">
                      <span className="text-xs text-stone-400">Total Photographers</span>
                      <span className="text-lg font-bold">{stats?.photographers?.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-stone-800/30 rounded-xl">
                      <span className="text-xs text-stone-400">Total Clients</span>
                      <span className="text-lg font-bold">{stats?.clients?.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-stone-800/30 rounded-xl">
                      <span className="text-xs text-stone-400">Conversion Rate</span>
                      <span className="text-lg font-bold">
                        {stats?.bookings?.total > 0 
                          ? ((stats?.bookings?.paid / stats?.bookings?.total) * 100).toFixed(1) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// ==================== CLIENT GALLERY PAGE ====================
const ClientGalleryPage = () => {
  const { jobId } = useParams();
  const { user, userType, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [showFilterFavorites, setShowFilterFavorites] = useState(false);
  const [showStylingPanel, setShowStylingPanel] = useState(false);
  const [heroImpact, setHeroImpact] = useState(64);
  const [allowDownloads, setAllowDownloads] = useState(true);
  const [allowFavorites, setAllowFavorites] = useState(true);
  const [allowSharing, setAllowSharing] = useState(true);
  const [paletteName, setPaletteName] = useState('champagne');
  const [editorialCaption, setEditorialCaption] = useState('');
  const [showInsightPanel, setShowInsightPanel] = useState(false);
  const [notice, setNotice] = useState(null);
  const galleryGridRef = useRef(null);

  useEffect(() => {
    const loadJob = async () => {
      if (!token || !jobId) return;
      try {
        const response = await axios.get(`${API}/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJob(response.data);
        setAllowDownloads(response.data.allow_downloads !== false && !response.data.is_preview);
        const favoritesAllowed = response.data.allow_favorites !== false;
        setAllowFavorites(favoritesAllowed);
        if (!favoritesAllowed) {
          setFavorites(new Set());
          setShowFilterFavorites(false);
        }
        setAllowSharing(response.data.allow_sharing !== false);
      } catch (error) {
        console.error('Failed to load gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    loadJob();
  }, [token, jobId]);

  const galleryPalettes = {
    champagne: {
      background: '#f1e7de',
      surface: '#fffdf8',
      muted: '#a39a91',
      accent: '#ef4444',
      ink: '#1c1917',
      veil: 'rgba(245, 238, 230, 0.44)'
    },
    porcelain: {
      background: '#f6f5f0',
      surface: '#ffffff',
      muted: '#9b9892',
      accent: '#1c1917',
      ink: '#171412',
      veil: 'rgba(250, 250, 247, 0.48)'
    },
    editorial: {
      background: '#e9ded3',
      surface: '#fffaf4',
      muted: '#998c82',
      accent: '#d94a4a',
      ink: '#1b1714',
      veil: 'rgba(239, 229, 220, 0.34)'
    }
  };
  const activePalette = galleryPalettes[paletteName];

  const showGalleryNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 2600);
  };

  const toggleFavorite = (index) => {
    if (!allowFavorites) {
      showGalleryNotice('Favorites are currently disabled for this gallery.');
      return;
    }

    const newFavorites = new Set(favorites);
    if (newFavorites.has(index)) {
      newFavorites.delete(index);
    } else {
      newFavorites.add(index);
    }
    setFavorites(newFavorites);
    setShowInsightPanel(true);
  };

  const images = job?.delivered_images || [];
  const galleryItems = images.map((src, index) => ({
    src,
    url: resolveImageUrl(src),
    fullUrl: getOptimizedImageUrl(src, 'full'),
    heroUrl: getOptimizedImageUrl(src, 'hero'),
    thumbUrl: getOptimizedImageUrl(src, 'gallery'),
    index
  })).filter(item => item.url);
  const heroImage = galleryItems[0]?.heroUrl || 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&q=80&w=1400';
  const deliveredDate = job?.completed_at || job?.created_at;
  const portalRoute = userType === 'photographer' ? '/pro' : '/dashboard';
  const clientName = job?.client_name || user?.name || 'Client';
  const galleryTitle = job?.title || `${clientName} Gallery`;
  const heroDisplayTitle = galleryTitle.replace(/\s+(wedding|gallery|collection)$/i, '').trim() || galleryTitle;
  const favoriteItems = galleryItems.filter(item => favorites.has(item.index));
  const displayItems = showFilterFavorites ? favoriteItems : galleryItems;
  const heroOverlay = `rgba(18, 16, 14, ${Math.max(0.22, 0.74 - heroImpact / 125)})`;
  const caption = editorialCaption || job?.description || 'A cinematic documentation of love and legacy. Curated specifically for your private family archive.';
  const isPreviewGallery = job?.is_preview || job?.gallery_type === 'preview';
  const canFinalSignOff = isPreviewGallery && userType === 'client' && job?.booking_id;
  const canConfigureGallery = userType === 'photographer' && user?.id === job?.photographer_id;

  const downloadImage = (url, index) => {
    if (!allowDownloads) {
      showGalleryNotice('Downloads are currently disabled for this gallery.');
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = getOptimizedImageUrl(url, 'full') || url;
    anchor.download = `${galleryTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index + 1}`;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const downloadCollection = (items) => {
    if (!allowDownloads) {
      showGalleryNotice('Downloads are currently disabled for this gallery.');
      return;
    }
    if (items.length === 0) {
      showGalleryNotice('Select at least one image first.');
      return;
    }
    items.forEach((item, downloadIndex) => {
      window.setTimeout(() => downloadImage(item.url, item.index), downloadIndex * 150);
    });
    showGalleryNotice(`${items.length} image${items.length === 1 ? '' : 's'} queued for download.`);
  };

  const shareCollection = async () => {
    if (!allowSharing) {
      showGalleryNotice('Sharing is currently disabled for this gallery.');
      return;
    }

    try {
      await copyTextToClipboard(window.location.href);
      showGalleryNotice('Private gallery link copied.');
    } catch (error) {
      console.error('Failed to copy gallery link:', error);
      showGalleryNotice('Could not copy the gallery link.');
    }
  };

  const scrollToGallery = () => {
    galleryGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const generateEditorialCaption = () => {
    setEditorialCaption(`The indelible archive of ${clientName}. ${galleryTitle}: where quiet emotion becomes legacy, captured for timeless reverence.`);
    showGalleryNotice('Editorial caption generated.');
  };

  const saveGalleryConfiguration = async () => {
    if (!canConfigureGallery) {
      setShowStylingPanel(false);
      showGalleryNotice('Only the photographer can update gallery settings.');
      return;
    }

    try {
      const response = await axios.put(`${API}/galleries/${job.id}`, {
        allow_downloads: allowDownloads && !isPreviewGallery,
        allow_favorites: allowFavorites,
        allow_sharing: allowSharing
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJob(response.data);
      setShowStylingPanel(false);
      showGalleryNotice('Gallery styling saved.');
    } catch (error) {
      console.error('Failed to save gallery configuration:', error);
      showGalleryNotice(error.response?.data?.detail || 'Could not save gallery settings.');
    }
  };

  const handleImageComment = async (item) => {
    if (!isPreviewGallery || userType !== 'client') return;
    const comment = window.prompt(`Comment for Image ${item.index + 1}`, '');
    if (!comment?.trim()) return;
    try {
      await api.addGalleryComment(job.id, String(item.index), {
        comment: comment.trim(),
        image_index: item.index
      }, token);
      showGalleryNotice('Comment sent to the photographer.');
    } catch (error) {
      console.error('Failed to add image comment:', error);
      showGalleryNotice(error.response?.data?.detail || 'Could not send comment.');
    }
  };

  const handleFinalSignoff = async () => {
    if (!canFinalSignOff) return;
    if (!window.confirm('Final sign off locks further preview revisions. Continue?')) return;
    try {
      await api.finalSignoff(job.booking_id, token);
      setJob({ ...job, status: 'approvedForFinalDelivery', final_signoff_at: new Date().toISOString() });
      showGalleryNotice('Final sign-off sent. Your photographer can now deliver the final gallery.');
    } catch (error) {
      console.error('Failed to sign off gallery:', error);
      showGalleryNotice(error.response?.data?.detail || 'Could not complete final sign-off.');
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center aura-site">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center aura-site">
        <div className="text-center">
          <Images className="w-16 h-16 text-stone-200 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Gallery Not Found</h2>
          <p className="text-stone-500 mb-6">This gallery doesn't exist or you don't have access.</p>
          <button onClick={() => navigate(portalRoute)} className="aura-cta px-6 py-3 font-medium">
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="aura-client-gallery"
      style={{
        '--gallery-bg': activePalette.background,
        '--gallery-surface': activePalette.surface,
        '--gallery-muted': activePalette.muted,
        '--gallery-accent': activePalette.accent,
        '--gallery-ink': activePalette.ink,
        '--gallery-veil': activePalette.veil,
        '--gallery-hero-overlay': heroOverlay
      }}
    >
      {notice && (
        <div className="aura-gallery-toast">
          <CheckCircle className="w-4 h-4" />
          <span>{notice}</span>
        </div>
      )}

      <header className="aura-gallery-hero">
        <img src={heroImage} alt={galleryTitle} className="aura-gallery-hero-image" />
        <div className="aura-gallery-hero-wash"></div>
        <div className="aura-gallery-hero-top">
          <Link to="/" className="aura-gallery-brand">
            <span>Private Collection</span>
            <strong>{galleryTitle}</strong>
          </Link>
          <div className="aura-gallery-hero-actions">
            {canConfigureGallery && (
              <button
                onClick={() => setShowStylingPanel(true)}
                className="aura-gallery-orb"
                title="Gallery styling"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => navigate(portalRoute)}
              className="aura-gallery-orb"
              title="Back to portal"
            >
              <Lock className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="aura-gallery-hero-content">
          <p className="aura-gallery-meta">
            <span>Delivered {deliveredDate ? new Date(deliveredDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently'}</span>
            <i></i>
            <span>Aura Studio x {job.category || 'Wedding'} Collection</span>
          </p>
          <h1 className="aura-gallery-title">{heroDisplayTitle}</h1>
          <p className="aura-gallery-caption">{caption}</p>
        </div>

        <button className="aura-gallery-scroll" onClick={scrollToGallery}>
          Scroll to view
          <ArrowRight className="w-4 h-4 rotate-90" />
        </button>
      </header>

      <main className="aura-gallery-body">
        <section className="aura-gallery-command-bar" aria-label="Gallery actions">
          <div className="aura-gallery-stat">
            <span>Collection Size</span>
            <strong>{galleryItems.length} Assets</strong>
          </div>
          <div className="aura-gallery-divider"></div>
          <button
            className={`aura-gallery-stat aura-gallery-stat-button ${showFilterFavorites ? 'is-active' : ''}`}
            onClick={() => setShowFilterFavorites(!showFilterFavorites)}
            disabled={!allowFavorites}
          >
            <span>Favorites</span>
            <strong>({favorites.size})</strong>
          </button>
          <div className="aura-gallery-spacer"></div>
          <button
            className="aura-gallery-pill"
            onClick={() => setShowInsightPanel(true)}
          >
            <Sparkles className="w-4 h-4" />
            AI Selection Help
          </button>
          {canFinalSignOff && (
            <button
              className="aura-gallery-pill"
              onClick={handleFinalSignoff}
              disabled={job?.status === 'approvedForFinalDelivery'}
            >
              <Check className="w-4 h-4" />
              Final Sign Off
            </button>
          )}
          <button
            className="aura-gallery-pill"
            onClick={shareCollection}
            disabled={!allowSharing}
          >
            <Copy className="w-4 h-4" />
            Share Collection
          </button>
          <button
            className="aura-gallery-pill"
            onClick={() => downloadCollection(favoriteItems)}
            disabled={favoriteItems.length === 0 || !allowDownloads}
          >
            <Heart className="w-4 h-4 fill-current" />
            Export Selected
          </button>
          <button
            className="aura-gallery-archive"
            onClick={() => downloadCollection(galleryItems)}
            disabled={galleryItems.length === 0 || !allowDownloads}
          >
            <CloudDownload className="w-4 h-4" />
            Download Full Archive
          </button>
        </section>

        {showInsightPanel && (
          <section className="aura-gallery-insight" aria-label="Aura aesthetic insight">
            <div className="aura-gallery-insight-icon">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <p className="aura-gallery-insight-label">Aura Aesthetic Insight</p>
              <p className="aura-gallery-insight-copy">
                {favorites.size > 0
                  ? 'Your selection gravitates towards elevated narrative, intimate framing, and cinematic grace. These favorites carry a refined editorial rhythm for your final archive.'
                  : 'Start favoriting images and Aura will help you read the visual story emerging inside your private collection.'}
              </p>
              <button onClick={() => setShowInsightPanel(false)} className="aura-gallery-dismiss">
                Dismiss
              </button>
            </div>
          </section>
        )}

        <section ref={galleryGridRef} className="aura-gallery-grid-section">
          {displayItems.length === 0 ? (
            <div className="aura-gallery-empty">
              <Images className="w-12 h-12" />
              <h2>{showFilterFavorites ? 'No Favorites Yet' : 'No Images Delivered Yet'}</h2>
              <p>{showFilterFavorites ? 'Tap the hearts on images you love and they will collect here.' : 'Your images will appear here after delivery.'}</p>
            </div>
          ) : (
            <div className="aura-gallery-grid">
              {displayItems.map((item, displayIndex) => {
                const isFav = favorites.has(item.index);
                return (
                  <article
                    key={`${item.src}-${item.index}`}
                    className={`aura-gallery-tile ${displayIndex % 5 === 1 ? 'is-wide' : ''}`}
                    onClick={(event) => {
                      if (event.target.closest('button')) return;
                      handleImageComment(item);
                    }}
                  >
                    <img
                      src={item.thumbUrl}
                      alt={`Gallery image ${item.index + 1}`}
                      loading="lazy"
                    />
                    {displayIndex === 1 && (
                      <div className="aura-gallery-ai-tag">
                        <Sparkles className="w-3 h-3" />
                        AI Tag: Portrait
                      </div>
                    )}
	                    <button
	                      onClick={() => toggleFavorite(item.index)}
	                      className={`aura-gallery-heart ${isFav ? 'is-favorite' : ''}`}
	                      disabled={!allowFavorites}
	                      data-testid={`fav-btn-${item.index}`}
	                      title={!allowFavorites ? 'Favorites disabled' : isFav ? 'Remove favorite' : 'Favorite image'}
	                    >
                      <Heart className={isFav ? 'fill-current' : ''} />
                    </button>
                    {isFav && (
                      <div className="aura-gallery-selection">
                        <span></span>
                        <strong>{favorites.size} Selected</strong>
                        <button onClick={() => downloadCollection(favoriteItems)} disabled={!allowDownloads}>
                          Download Selection
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <div className="aura-gallery-floating-tools" aria-label="Gallery tools">
        {canConfigureGallery && (
          <button onClick={() => setShowStylingPanel(true)} title="Open styling">
            <Settings className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => setShowFilterFavorites(!showFilterFavorites)} disabled={!allowFavorites} title="Toggle favorites">
          <Images className="w-4 h-4" />
        </button>
        <button onClick={shareCollection} disabled={!allowSharing} title="Share collection">
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={() => setShowInsightPanel(true)} title="Selection insight">
          <Sparkles className="w-4 h-4" />
        </button>
      </div>

      {showStylingPanel && canConfigureGallery && (
        <aside className="aura-gallery-drawer" aria-label="Gallery styling">
          <div className="aura-gallery-drawer-header">
            <h2 className="serif italic">Gallery Styling</h2>
            <button onClick={() => setShowStylingPanel(false)} title="Close styling panel">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="aura-gallery-drawer-card">
            <div className="aura-gallery-drawer-label">
              <span>AI Content</span>
              <em>Pro</em>
            </div>
            <button onClick={generateEditorialCaption} className="aura-gallery-drawer-action">
              <Sparkles className="w-4 h-4" />
              Generate Editorial Caption
            </button>
          </div>

          <div className="aura-gallery-control-group">
            <p>Color Palette</p>
            <div className="aura-gallery-swatches">
              {Object.entries(galleryPalettes).map(([name, palette]) => (
                <button
                  key={name}
                  onClick={() => setPaletteName(name)}
                  className={paletteName === name ? 'is-selected' : ''}
                  style={{ background: palette.surface, borderColor: palette.accent }}
                  title={`${name} palette`}
                ></button>
              ))}
            </div>
          </div>

          <div className="aura-gallery-control-group">
            <p>Hero Impact</p>
            <input
              type="range"
              min="35"
              max="90"
              value={heroImpact}
              onChange={(event) => setHeroImpact(Number(event.target.value))}
              className="aura-gallery-range"
            />
          </div>

          <div className="aura-gallery-control-group">
            <p>Client Permissions</p>
            <label className="aura-gallery-checkbox">
              <span>Allow Downloads</span>
              <input
                type="checkbox"
                checked={allowDownloads && !isPreviewGallery}
                disabled={isPreviewGallery}
                onChange={(event) => setAllowDownloads(event.target.checked)}
              />
            </label>
            <label className="aura-gallery-checkbox">
              <span>Allow Favorites</span>
              <input
                type="checkbox"
                checked={allowFavorites}
                onChange={(event) => {
                  setAllowFavorites(event.target.checked);
                  if (!event.target.checked) {
                    setFavorites(new Set());
                    setShowFilterFavorites(false);
                  }
                }}
              />
            </label>
            <label className="aura-gallery-checkbox">
              <span>Allow Sharing</span>
              <input
                type="checkbox"
                checked={allowSharing}
                onChange={(event) => setAllowSharing(event.target.checked)}
              />
            </label>
          </div>

          <button onClick={saveGalleryConfiguration} className="aura-gallery-save">
            Save Configuration
          </button>
        </aside>
      )}
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  return children;
};

// ==================== APP ROUTER (Handles OAuth callback) ====================
const AppRouter = () => {
  // CRITICAL: Check for session_id synchronously during render to prevent race conditions
  // This runs BEFORE any useEffect, ensuring OAuth callback is handled first
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/portal" element={<Navigate to="/register" replace />} />
      <Route path="/client" element={<Navigate to="/dashboard" replace />} />
      <Route path="/client/:section" element={<Navigate to="/dashboard" replace />} />
      <Route path="/portfolio-public" element={<Navigate to="/" replace />} />
      <Route path="/photographer/:id" element={<PhotographerProfilePage />} />
      <Route 
        path="/gallery/:jobId" 
        element={
          <ProtectedRoute>
            <ClientGalleryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pro" 
        element={
          <ProtectedRoute>
            <PhotographerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pro/:section" 
        element={
          <ProtectedRoute>
            <PhotographerDashboard />
          </ProtectedRoute>
        } 
      />
	      <Route 
	        path="/admin" 
	        element={
	          <ProtectedRoute>
	            <AdminDashboard />
	          </ProtectedRoute>
	        } 
	      />
	      <Route path="*" element={<Navigate to="/" replace />} />
	    </Routes>
  );
};

// ==================== MAIN APP ====================
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
